/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useEffect, useRef, useState } from "react";
import axios from "../api/axiosInstance"; // token JWT incluido automáticamente
import "../Reservations.css";
import Select from "react-select";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePmsTheme } from "../Components/PmsThemeContext";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import {
  currencyAmountStep,
  getCurrencyMinorUnits,
  hasValidCurrencyPrecision,
  normalizeCurrencyCode,
} from "../currency/currencyPrecision";
import RoomRateForm from "./RoomRateForm"; // ajusta ruta si está en otra carpeta
import { DEMO_BUSINESS_UNIT_ID, isDemoMode, readDemoUser } from "../demo/demoMode";
import "../RevenueManagement.css";


const REVENUE_MANAGEMENT_MESSAGES = {
  en: {
    values: {
      any: "Any",
      yes: "Yes",
      no: "No",
      none: "None",
      notAvailable: "N/A",
      noClient: "No client",
      exact: "Exact",
      upto: "Up to",
      all: "All",
      multiple: "Multiple",
      unknownCategory: "Unknown category",
      unknownRate: "Unknown rate",
      rateWithoutId: "Rate without ID",
      group: "Group",
      id: "ID",
      active: "active",
      inactive: "inactive",
      unknownError: "Unknown error",
    },
    rates: {
      title: "Room Rates Overview",
      subtitleLoaded: (count) => `${count} rates loaded`,
      add: "+ Add",
      bulkApply: "Bulk Apply",
      stopSellsOverview: "Stop Sells Overview",
      searchPlaceholder: "Search by rate, client, category, status, dates...",
      noMatch: "No room rates match the current search/filters.",
      filterBy: (header) => `Filter by ${header}`,
      headers: {
        roomRate: "Room Rate",
        derivedRates: "Derived Rates",
        client: "Client",
        roomCategory: "Room Category",
        status: "Status",
        adults: "Adults",
        maxOccupancy: "Max Occupancy",
        children: "Children",
        showInBookingEngine: "Show in booking engine",
        refundable: "Refundable",
        penalty: "Penalty",
        minPrice: "Min Price",
        maxPrice: "Max Price",
        defaultMinStay: "Default Min Stay",
        defaultMaxStay: "Default Max Stay",
        startDate: "Start Date",
        endDate: "End Date",
      },
    },
    filters: {
      clear: "Clear",
      searchIn: (title) => `Search in ${String(title || "").toLowerCase()}...`,
      noMatchingOptions: "No matching options.",
      selectValuesHint: "Select one or more values.",
      selected: (count) => `${count} selected`,
    },
    stopSells: {
      kicker: "Revenue Management",
      title: "Stop Sells Overview",
      subtitle: "Consecutive stop sells are merged by room category, room rate, and automatic flag.",
      backToRates: "Back to Room Rates",
      kpis: {
        activeNights: { label: "Active Nights", foot: "Days blocked from today onward" },
        activeRanges: { label: "Active Ranges", foot: "Merged consecutive ranges" },
        categories: { label: "Categories", foot: "Room categories impacted" },
        roomRates: { label: "Room Rates", foot: "Rate plans impacted" },
        lastUpdate: { label: "Last Update", foot: "Latest change detected" },
      },
      loadingTitle: "Loading stop sells...",
      loadingText: "Fetching active ranges for this business unit.",
      emptyTitle: "No active stop sells",
      emptyText: "There are no current or future stop sells to display.",
      headers: {
        roomCategory: "Room Category",
        roomRate: "Room Rate",
        from: "From",
        to: "To",
        nights: "Nights",
        automatic: "Automatic",
        reason: "Reason",
        lastUpdate: "Last update",
        action: "Action",
      },
      openRate: "Open rate",
    },
    bulk: {
      modalTitle: "Bulk / Apply to multiple rates",
      stepOf: (step, title) => `Step ${step} of 4 - ${title}`,
      actionLabels: {
        add_stop_sell: "Add Stop Sell",
        remove_stop_sell: "Remove Stop Sell (range cut)",
        upsert_rate_price: "Add / Overwrite Rate Price (range)",
        set_min_stay: "Set Min Stay",
        set_max_stay: "Set Max Stay",
      },
      stepTitles: {
        1: "Choose Action",
        2: "Choose Room Rates",
        3: "Choose Dates",
        4: "Set Values",
      },
      step1Title: "Step 1. Select action",
      step2Title: "Step 2. Choose room rates",
      step3Title: "Step 3. Select date range",
      step4Title: "Step 4. Set values",
      chooseActionHelp: "Choose action first. The next step adapts room-rate selection to the chosen action.",
      selectActionPlaceholder: "Select action...",
      close: "Close",
      searchRatePlaceholder: "Search rate name / code...",
      selectAll: "Select all",
      clearSelection: "Clear",
      derivedOnlyHidden: "All matching rates are derived and cannot be modified in price action.",
      derivedHiddenCount: (count) => `${count} derived room rate(s) hidden: they cannot be modified in price action.`,
      noRatesMatch: "No rates match your search.",
      reasonOptional: "Reason (optional)",
      reasonPlaceholder: "Optional reason for stop sell...",
      automaticFalseHint: "is_automatic is always sent as false.",
      noExtraInputs: "No extra inputs required for range cut.",
      price: "Price",
      minStayOptional: "Min stay (optional)",
      maxStayOptional: "Max stay (optional)",
      minStay: "Min stay",
      maxStay: "Max stay",
      selectedLabel: "Selected",
      actionLabel: "Action",
      cancel: "Cancel",
      back: "Back",
      next: "Next",
      apply: "Apply",
      applying: "Applying...",
      backgroundAction: "Running in background...",
      backgroundStarted: (action, count) => `${action} queued for ${count} room rate(s). You can continue working.`,
      backgroundProgress: (completed, total) => `${completed} of ${total} room rates processed`,
      backgroundVerifying: "The server is still finishing the change. Verifying the final result in the background...",
      backgroundComplete: (count) => `Background update completed for ${count} room rate(s).`,
      backgroundFailed: (failed, total) => `${failed} of ${total} room rates could not be confirmed.`,
      backgroundFailureTitle: "Background update needs attention",
      backgroundFailureIntro: "These room rates were checked repeatedly and the requested final state could not be confirmed:",
      backgroundUnconfirmed: "The server did not confirm the requested final state within the verification window.",
      backgroundDismiss: "Close",
      step1Hint: "Select an action in Step 1 to show inputs.",
      startDate: "Start date",
      endDate: "End date",
      tableHeaders: {
        roomRate: "Room Rate",
        client: "Client",
        roomCategory: "Room Category",
      },
    },
    alerts: {
      selectRoomRate: "Please select at least one room rate.",
      selectBulkAction: "Please select a bulk action.",
      derivedRatePriceNotAllowed: "Derived room rates cannot be modified in price action. Please remove them from selection.",
      selectDates: "Please select start and end dates.",
      endDateAfterStart: "End date must be after start date.",
      validPrice: "Please enter a valid price.",
      validPricePrecision: (currency, decimals) =>
        `${currency} accepts ${decimals} decimal places.`,
      mixedRateCurrencies: "Select room rates with the same currency before applying one price.",
      validMinMaxIntegers: "Min stay / Max stay must be valid integers.",
      optionalMinStayMin: "Optional Min stay must be >= 1.",
      optionalMaxStayMin: "Optional Max stay must be >= 1.",
      optionalMaxStayBelowMin: "Optional Max stay cannot be lower than Min stay.",
      validMinStay: "Please enter a valid Min stay (integer >= 1).",
      validMaxStay: "Please enter a valid Max stay (integer >= 1).",
      bulkActionNotConfigured: "Bulk action is not configured.",
      bulkDateRangeValid: "Please select a valid start and end date.",
      bulkSuccess: (actionLabel, count) => `Bulk action "${actionLabel}" applied to ${count} room rate(s).`,
      bulkErrors: (successCount, failedCount) =>
        `Bulk action finished with errors.\nSuccess: ${successCount}\nFailed: ${failedCount}\nCheck console [REV-MGMT][BULK][ACTION].`,
    },
    confirm: {
      action: "Action",
      ratesSelected: "Rates selected",
      dateRange: "Date range",
      inputs: "Inputs",
      proceed: "Proceed?",
    },
  },
  es: {
    values: {
      any: "Cualquiera",
      yes: "S\u00ed",
      no: "No",
      none: "Ninguno",
      notAvailable: "N/D",
      noClient: "Sin cliente",
      exact: "Exacto",
      upto: "Hasta",
      all: "Todos",
      multiple: "M\u00faltiples",
      unknownCategory: "Categor\u00eda desconocida",
      unknownRate: "Tarifa desconocida",
      rateWithoutId: "Tarifa sin ID",
      group: "Grupo",
      id: "ID",
      active: "activo",
      inactive: "inactivo",
      unknownError: "Error desconocido",
    },
    rates: {
      title: "Resumen de tarifas",
      subtitleLoaded: (count) => `${count} tarifas cargadas`,
      add: "+ A\u00f1adir",
      bulkApply: "Aplicaci\u00f3n masiva",
      stopSellsOverview: "Resumen de stop sells",
      searchPlaceholder: "Buscar por tarifa, cliente, categor\u00eda, estado, fechas...",
      noMatch: "Ninguna tarifa coincide con la b\u00fasqueda o los filtros actuales.",
      filterBy: (header) => `Filtrar por ${header}`,
      headers: {
        roomRate: "Tarifa",
        derivedRates: "Tarifas derivadas",
        client: "Cliente",
        roomCategory: "Categor\u00eda",
        status: "Estado",
        adults: "Adultos",
        maxOccupancy: "Ocupaci\u00f3n m\u00e1xima",
        children: "Ni\u00f1os",
        showInBookingEngine: "Mostrar en motor de reservas",
        refundable: "Reembolsable",
        penalty: "Penalizaci\u00f3n",
        minPrice: "Precio m\u00ednimo",
        maxPrice: "Precio m\u00e1ximo",
        defaultMinStay: "Estancia m\u00ednima por defecto",
        defaultMaxStay: "Estancia m\u00e1xima por defecto",
        startDate: "Fecha inicio",
        endDate: "Fecha fin",
      },
    },
    filters: {
      clear: "Limpiar",
      searchIn: (title) => `Buscar en ${String(title || "").toLowerCase()}...`,
      noMatchingOptions: "No hay opciones coincidentes.",
      selectValuesHint: "Selecciona uno o varios valores.",
      selected: (count) => `${count} seleccionados`,
    },
    stopSells: {
      kicker: "Revenue Management",
      title: "Resumen de stop sells",
      subtitle: "Los stop sells consecutivos se agrupan por categor\u00eda, tarifa y marca autom\u00e1tica.",
      backToRates: "Volver a tarifas",
      kpis: {
        activeNights: { label: "Noches activas", foot: "D\u00edas bloqueados desde hoy en adelante" },
        activeRanges: { label: "Rangos activos", foot: "Rangos consecutivos agrupados" },
        categories: { label: "Categor\u00edas", foot: "Categor\u00edas afectadas" },
        roomRates: { label: "Tarifas", foot: "Planes tarifarios afectados" },
        lastUpdate: { label: "\u00daltima actualizaci\u00f3n", foot: "\u00daltimo cambio detectado" },
      },
      loadingTitle: "Cargando stop sells...",
      loadingText: "Obteniendo los rangos activos de esta unidad de negocio.",
      emptyTitle: "No hay stop sells activos",
      emptyText: "No hay stop sells actuales o futuros para mostrar.",
      headers: {
        roomCategory: "Categor\u00eda",
        roomRate: "Tarifa",
        from: "Desde",
        to: "Hasta",
        nights: "Noches",
        automatic: "Autom\u00e1tico",
        reason: "Motivo",
        lastUpdate: "\u00daltima actualizaci\u00f3n",
        action: "Acci\u00f3n",
      },
      openRate: "Abrir tarifa",
    },
    bulk: {
      modalTitle: "Aplicaci\u00f3n masiva sobre varias tarifas",
      stepOf: (step, title) => `Paso ${step} de 4 - ${title}`,
      actionLabels: {
        add_stop_sell: "A\u00f1adir Stop Sell",
        remove_stop_sell: "Quitar Stop Sell (corte por rango)",
        upsert_rate_price: "A\u00f1adir / sobrescribir precio (rango)",
        set_min_stay: "Definir estancia m\u00ednima",
        set_max_stay: "Definir estancia m\u00e1xima",
      },
      stepTitles: {
        1: "Elegir acci\u00f3n",
        2: "Elegir tarifas",
        3: "Elegir fechas",
        4: "Definir valores",
      },
      step1Title: "Paso 1. Seleccionar acci\u00f3n",
      step2Title: "Paso 2. Elegir tarifas",
      step3Title: "Paso 3. Seleccionar rango de fechas",
      step4Title: "Paso 4. Definir valores",
      chooseActionHelp: "Elige primero la acci\u00f3n. El siguiente paso adapta la selecci\u00f3n de tarifas a la acci\u00f3n elegida.",
      selectActionPlaceholder: "Selecciona una acci\u00f3n...",
      close: "Cerrar",
      searchRatePlaceholder: "Buscar nombre o c\u00f3digo de tarifa...",
      selectAll: "Seleccionar todo",
      clearSelection: "Limpiar",
      derivedOnlyHidden: "Todas las tarifas coincidentes son derivadas y no se pueden modificar en la acci\u00f3n de precio.",
      derivedHiddenCount: (count) => `${count} tarifa(s) derivada(s) ocultas: no se pueden modificar en la acci\u00f3n de precio.`,
      noRatesMatch: "Ninguna tarifa coincide con tu b\u00fasqueda.",
      reasonOptional: "Motivo (opcional)",
      reasonPlaceholder: "Motivo opcional del stop sell...",
      automaticFalseHint: "is_automatic siempre se env\u00eda como false.",
      noExtraInputs: "No se necesitan campos extra para el corte por rango.",
      price: "Precio",
      minStayOptional: "Estancia m\u00ednima (opcional)",
      maxStayOptional: "Estancia m\u00e1xima (opcional)",
      minStay: "Estancia m\u00ednima",
      maxStay: "Estancia m\u00e1xima",
      selectedLabel: "Seleccionadas",
      actionLabel: "Acci\u00f3n",
      cancel: "Cancelar",
      back: "Atr\u00e1s",
      next: "Siguiente",
      apply: "Aplicar",
      applying: "Aplicando...",
      backgroundAction: "En segundo plano...",
      backgroundStarted: (action, count) => `${action} encolada para ${count} tarifa(s). Puedes seguir trabajando.`,
      backgroundProgress: (completed, total) => `${completed} de ${total} tarifas procesadas`,
      backgroundVerifying: "El servidor sigue terminando el cambio. Se comprobará el resultado final en segundo plano...",
      backgroundComplete: (count) => `Actualización en segundo plano completada para ${count} tarifa(s).`,
      backgroundFailed: (failed, total) => `No se pudo confirmar el cambio en ${failed} de ${total} tarifa(s).`,
      backgroundFailureTitle: "La actualización en segundo plano requiere atención",
      backgroundFailureIntro: "Estas tarifas se comprobaron varias veces y no se pudo confirmar el estado final solicitado:",
      backgroundUnconfirmed: "El servidor no confirmó el estado final solicitado dentro del periodo de comprobación.",
      backgroundDismiss: "Cerrar",
      step1Hint: "Selecciona una acci\u00f3n en el paso 1 para mostrar los campos.",
      startDate: "Fecha inicio",
      endDate: "Fecha fin",
      tableHeaders: {
        roomRate: "Tarifa",
        client: "Cliente",
        roomCategory: "Categor\u00eda",
      },
    },
    alerts: {
      selectRoomRate: "Selecciona al menos una tarifa.",
      selectBulkAction: "Selecciona una acci\u00f3n masiva.",
      derivedRatePriceNotAllowed: "Las tarifas derivadas no se pueden modificar en la acci\u00f3n de precio. Qu\u00edtalas de la selecci\u00f3n.",
      selectDates: "Selecciona fecha de inicio y de fin.",
      endDateAfterStart: "La fecha de fin debe ser posterior a la de inicio.",
      validPrice: "Introduce un precio v\u00e1lido.",
      validPricePrecision: (currency, decimals) =>
        `${currency} admite ${decimals} decimales.`,
      mixedRateCurrencies: "Selecciona tarifas con la misma moneda antes de aplicar un único precio.",
      validMinMaxIntegers: "La estancia m\u00ednima y m\u00e1xima deben ser enteros v\u00e1lidos.",
      optionalMinStayMin: "La estancia m\u00ednima opcional debe ser >= 1.",
      optionalMaxStayMin: "La estancia m\u00e1xima opcional debe ser >= 1.",
      optionalMaxStayBelowMin: "La estancia m\u00e1xima opcional no puede ser inferior a la m\u00ednima.",
      validMinStay: "Introduce una estancia m\u00ednima v\u00e1lida (entero >= 1).",
      validMaxStay: "Introduce una estancia m\u00e1xima v\u00e1lida (entero >= 1).",
      bulkActionNotConfigured: "La acci\u00f3n masiva no est\u00e1 configurada.",
      bulkDateRangeValid: "Selecciona una fecha de inicio y fin v\u00e1lida.",
      bulkSuccess: (actionLabel, count) => `La acci\u00f3n masiva "${actionLabel}" se aplic\u00f3 a ${count} tarifa(s).`,
      bulkErrors: (successCount, failedCount) =>
        `La acci\u00f3n masiva termin\u00f3 con errores.\nCorrectas: ${successCount}\nFallidas: ${failedCount}\nRevisa la consola [REV-MGMT][BULK][ACTION].`,
    },
    confirm: {
      action: "Acci\u00f3n",
      ratesSelected: "Tarifas seleccionadas",
      dateRange: "Rango de fechas",
      inputs: "Valores",
      proceed: "\u00bfContinuar?",
    },
  },
  ca: {
    values: {
      any: "Qualsevol",
      yes: "S\u00ed",
      no: "No",
      none: "Cap",
      notAvailable: "N/D",
      noClient: "Sense client",
      exact: "Exacte",
      upto: "Fins a",
      all: "Tots",
      multiple: "M\u00faltiples",
      unknownCategory: "Categoria desconeguda",
      unknownRate: "Tarifa desconeguda",
      rateWithoutId: "Tarifa sense ID",
      group: "Grup",
      id: "ID",
      active: "actiu",
      inactive: "inactiu",
      unknownError: "Error desconegut",
    },
    rates: {
      title: "Resum de tarifes",
      subtitleLoaded: (count) => `${count} tarifes carregades`,
      add: "+ Afegir",
      bulkApply: "Aplicaci\u00f3 massiva",
      stopSellsOverview: "Resum de stop sells",
      searchPlaceholder: "Cercar per tarifa, client, categoria, estat, dates...",
      noMatch: "Cap tarifa coincideix amb la cerca o els filtres actuals.",
      filterBy: (header) => `Filtrar per ${header}`,
      headers: {
        roomRate: "Tarifa",
        derivedRates: "Tarifes derivades",
        client: "Client",
        roomCategory: "Categoria",
        status: "Estat",
        adults: "Adults",
        maxOccupancy: "Ocupaci\u00f3 m\u00e0xima",
        children: "Infants",
        showInBookingEngine: "Mostrar al motor de reserves",
        refundable: "Reemborsable",
        penalty: "Penalitzaci\u00f3",
        minPrice: "Preu m\u00ednim",
        maxPrice: "Preu m\u00e0xim",
        defaultMinStay: "Estada m\u00ednima per defecte",
        defaultMaxStay: "Estada m\u00e0xima per defecte",
        startDate: "Data d'inici",
        endDate: "Data de fi",
      },
    },
    filters: {
      clear: "Netejar",
      searchIn: (title) => `Cercar a ${String(title || "").toLowerCase()}...`,
      noMatchingOptions: "No hi ha opcions coincidents.",
      selectValuesHint: "Selecciona un o diversos valors.",
      selected: (count) => `${count} seleccionats`,
    },
    stopSells: {
      kicker: "Revenue Management",
      title: "Resum de stop sells",
      subtitle: "Els stop sells consecutius s'agrupen per categoria, tarifa i marca autom\u00e0tica.",
      backToRates: "Tornar a tarifes",
      kpis: {
        activeNights: { label: "Nits actives", foot: "Dies bloquejats des d'avui endavant" },
        activeRanges: { label: "Rangs actius", foot: "Rangs consecutius agrupats" },
        categories: { label: "Categories", foot: "Categories afectades" },
        roomRates: { label: "Tarifes", foot: "Plans tarifaris afectats" },
        lastUpdate: { label: "Darrera actualitzaci\u00f3", foot: "Darrer canvi detectat" },
      },
      loadingTitle: "Carregant stop sells...",
      loadingText: "S'estan obtenint els rangs actius d'aquesta unitat de negoci.",
      emptyTitle: "No hi ha stop sells actius",
      emptyText: "No hi ha stop sells actuals o futurs per mostrar.",
      headers: {
        roomCategory: "Categoria",
        roomRate: "Tarifa",
        from: "Des de",
        to: "Fins a",
        nights: "Nits",
        automatic: "Autom\u00e0tic",
        reason: "Motiu",
        lastUpdate: "Darrera actualitzaci\u00f3",
        action: "Acci\u00f3",
      },
      openRate: "Obrir tarifa",
    },
    bulk: {
      modalTitle: "Aplicaci\u00f3 massiva sobre diverses tarifes",
      stepOf: (step, title) => `Pas ${step} de 4 - ${title}`,
      actionLabels: {
        add_stop_sell: "Afegir Stop Sell",
        remove_stop_sell: "Treure Stop Sell (tall per rang)",
        upsert_rate_price: "Afegir / sobrescriure preu (rang)",
        set_min_stay: "Definir estada m\u00ednima",
        set_max_stay: "Definir estada m\u00e0xima",
      },
      stepTitles: {
        1: "Triar acci\u00f3",
        2: "Triar tarifes",
        3: "Triar dates",
        4: "Definir valors",
      },
      step1Title: "Pas 1. Seleccionar acci\u00f3",
      step2Title: "Pas 2. Triar tarifes",
      step3Title: "Pas 3. Seleccionar rang de dates",
      step4Title: "Pas 4. Definir valors",
      chooseActionHelp: "Tria primer l'acci\u00f3. El pas seg\u00fcent adapta la selecci\u00f3 de tarifes a l'acci\u00f3 escollida.",
      selectActionPlaceholder: "Selecciona una acci\u00f3...",
      close: "Tancar",
      searchRatePlaceholder: "Cercar nom o codi de tarifa...",
      selectAll: "Seleccionar-ho tot",
      clearSelection: "Netejar",
      derivedOnlyHidden: "Totes les tarifes coincidents s\u00f3n derivades i no es poden modificar en l'acci\u00f3 de preu.",
      derivedHiddenCount: (count) => `${count} tarifa/es derivada/es ocultes: no es poden modificar en l'acci\u00f3 de preu.`,
      noRatesMatch: "Cap tarifa coincideix amb la teva cerca.",
      reasonOptional: "Motiu (opcional)",
      reasonPlaceholder: "Motiu opcional del stop sell...",
      automaticFalseHint: "is_automatic sempre s'envia com a false.",
      noExtraInputs: "No calen camps extra per al tall per rang.",
      price: "Preu",
      minStayOptional: "Estada m\u00ednima (opcional)",
      maxStayOptional: "Estada m\u00e0xima (opcional)",
      minStay: "Estada m\u00ednima",
      maxStay: "Estada m\u00e0xima",
      selectedLabel: "Seleccionades",
      actionLabel: "Acci\u00f3",
      cancel: "Cancel\u00b7lar",
      back: "Enrere",
      next: "Seg\u00fcent",
      apply: "Aplicar",
      applying: "Aplicant...",
      backgroundAction: "En segon pla...",
      backgroundStarted: (action, count) => `${action} en cua per a ${count} tarifa(es). Pots continuar treballant.`,
      backgroundProgress: (completed, total) => `${completed} de ${total} tarifes processades`,
      backgroundVerifying: "El servidor encara està acabant el canvi. Es comprovarà el resultat final en segon pla...",
      backgroundComplete: (count) => `Actualització en segon pla completada per a ${count} tarifa(es).`,
      backgroundFailed: (failed, total) => `No s'ha pogut confirmar el canvi en ${failed} de ${total} tarifa(es).`,
      backgroundFailureTitle: "L'actualització en segon pla requereix atenció",
      backgroundFailureIntro: "Aquestes tarifes s'han comprovat diverses vegades i no s'ha pogut confirmar l'estat final sol·licitat:",
      backgroundUnconfirmed: "El servidor no ha confirmat l'estat final sol·licitat dins del període de comprovació.",
      backgroundDismiss: "Tancar",
      step1Hint: "Selecciona una acci\u00f3 al pas 1 per mostrar els camps.",
      startDate: "Data d'inici",
      endDate: "Data de fi",
      tableHeaders: {
        roomRate: "Tarifa",
        client: "Client",
        roomCategory: "Categoria",
      },
    },
    alerts: {
      selectRoomRate: "Selecciona almenys una tarifa.",
      selectBulkAction: "Selecciona una acci\u00f3 massiva.",
      derivedRatePriceNotAllowed: "Les tarifes derivades no es poden modificar en l'acci\u00f3 de preu. Treu-les de la selecci\u00f3.",
      selectDates: "Selecciona data d'inici i de fi.",
      endDateAfterStart: "La data de fi ha de ser posterior a la d'inici.",
      validPrice: "Introdueix un preu v\u00e0lid.",
      validPricePrecision: (currency, decimals) =>
        `${currency} admet ${decimals} decimals.`,
      mixedRateCurrencies: "Selecciona tarifes amb la mateixa moneda abans d'aplicar un únic preu.",
      validMinMaxIntegers: "L'estada m\u00ednima i m\u00e0xima han de ser enters v\u00e0lids.",
      optionalMinStayMin: "L'estada m\u00ednima opcional ha de ser >= 1.",
      optionalMaxStayMin: "L'estada m\u00e0xima opcional ha de ser >= 1.",
      optionalMaxStayBelowMin: "L'estada m\u00e0xima opcional no pot ser inferior a la m\u00ednima.",
      validMinStay: "Introdueix una estada m\u00ednima v\u00e0lida (enter >= 1).",
      validMaxStay: "Introdueix una estada m\u00e0xima v\u00e0lida (enter >= 1).",
      bulkActionNotConfigured: "L'acci\u00f3 massiva no est\u00e0 configurada.",
      bulkDateRangeValid: "Selecciona una data d'inici i fi v\u00e0lida.",
      bulkSuccess: (actionLabel, count) => `L'acci\u00f3 massiva "${actionLabel}" s'ha aplicat a ${count} tarifa/es.`,
      bulkErrors: (successCount, failedCount) =>
        `L'acci\u00f3 massiva ha acabat amb errors.\nCorrectes: ${successCount}\nFallides: ${failedCount}\nRevisa la consola [REV-MGMT][BULK][ACTION].`,
    },
    confirm: {
      action: "Acci\u00f3",
      ratesSelected: "Tarifes seleccionades",
      dateRange: "Rang de dates",
      inputs: "Valors",
      proceed: "Continuar?",
    },
  },
};

const BULK_ACTION_VALUES = [
  "add_stop_sell",
  "remove_stop_sell",
  "upsert_rate_price",
  "set_min_stay",
  "set_max_stay",
];





const getDefaultRateColumnFilters = () => ({
  roomRate: [],
  derivedRates: [],
  client: [],
  roomCategory: [],
  status: [],
  adults: [],
  maxOccupancy: [],
  children: [],
  showInBookingEngine: [],
  refundable: [],
  penalty: [],
  minPrice: [],
  maxPrice: [],
  defaultMinStay: [],
  defaultMaxStay: [],
  startDate: [],
  endDate: [],
});

const formatMatchType = (matchType, copy) => {
  if (matchType === "exact") return copy.values.exact;
  if (matchType === "upto") return copy.values.upto;
  return "";
};

const formatMatchValue = (value, matchType, fallback, copy) => {
  if (value == null || value === "") return fallback;
  const prefix = formatMatchType(matchType, copy);
  return prefix ? `${prefix} ${value}` : String(value);
};

export const formatRevenueCurrencyValue = (value, currencyCode, currencyCatalog = []) => {
  if (value == null || value === "") return "-";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return `${num.toFixed(getCurrencyMinorUnits(currencyCode, currencyCatalog))} ${currencyCode}`;
};

export const formatRevenueAdjustmentValue = (
  value,
  adjustmentType,
  currencyCode,
  currencyCatalog = []
) => {
  const numeric = Number.parseFloat(value);
  if (!Number.isFinite(numeric)) return "-";
  const isPercentage = adjustmentType === "percent";
  const decimals = isPercentage
    ? 2
    : getCurrencyMinorUnits(currencyCode, currencyCatalog);
  return `${numeric.toFixed(decimals)} ${isPercentage ? "%" : currencyCode}`;
};

export const isValidRevenueCurrencyInput = (value, currencyCode, currencyCatalog = []) =>
  Number.isFinite(Number(String(value ?? "").replace(",", "."))) &&
  hasValidCurrencyPrecision(value, currencyCode, currencyCatalog);

export const resolveRevenueRateCurrency = (rate, baseCurrency = "EUR") =>
  normalizeCurrencyCode(rate?.currency) || normalizeCurrencyCode(baseCurrency) || "EUR";

export const resolveBulkRevenueRateCurrency = (
  selectedRateIds = [],
  rates = [],
  baseCurrency = "EUR"
) => {
  const selectedIds = new Set(selectedRateIds.map(Number));
  const currencies = Array.from(new Set(
    rates
      .filter((rate) => selectedIds.has(Number(rate?.id)))
      .map((rate) => resolveRevenueRateCurrency(rate, baseCurrency))
  ));
  return {
    currency: currencies.length === 1 ? currencies[0] : normalizeCurrencyCode(baseCurrency) || "EUR",
    currencies,
    mixed: currencies.length > 1,
  };
};

const formatRateStatus = (status, copy) => {
  const normalized = String(status || "").trim().toLowerCase();
  if (!normalized) return "-";
  if (normalized === "active") return copy.values.active;
  if (normalized === "inactive") return copy.values.inactive;
  return String(status);
};

const isDerivedRateForPriceBulk = (rate) => {
  if (!rate) return false;
  return Boolean(rate.base_rate_id || rate.base_rate?.id || rate.has_formula_link);
};


const REVENUE_DEMO_MESSAGES = {
  en: {
    readOnly: "Read-only demo. Rate changes are disabled.",
    stopSellsUnavailable: "Stop Sells Overview is not enabled in this demo.",
    loading: "Loading room rates...",
    loadError: "Room rates could not be loaded.",
    retry: "Retry",
  },
  es: {
    readOnly: "Demo de solo consulta. Los cambios de tarifas están deshabilitados.",
    stopSellsUnavailable: "El resumen general de stop sells no está habilitado en esta demo.",
    loading: "Cargando tarifas...",
    loadError: "No se han podido cargar las tarifas.",
    retry: "Reintentar",
  },
  ca: {
    readOnly: "Demo de només consulta. Els canvis de tarifes estan deshabilitats.",
    stopSellsUnavailable: "El resum general de stop sells no està habilitat en aquesta demo.",
    loading: "Carregant tarifes...",
    loadError: "No s'han pogut carregar les tarifes.",
    retry: "Torna-ho a provar",
  },
};

const RevenueManagement = ({ pendingAction = null, onPendingActionConsumed = null }) => {
  const navigate = useNavigate();
  const demoModeActive = isDemoMode();
  const currentUser = React.useMemo(() => demoModeActive
    ? readDemoUser()
    : JSON.parse(localStorage.getItem("palantir_user") || "null"), [demoModeActive]);
  const permissions = React.useMemo(() => currentUser?.permissions || {}, [currentUser]);
  const { themeId, languageId, language } = usePmsTheme();
  const { baseCurrency, currencyCatalog } = useCurrencyDisplay();
  const isLightTheme = themeId === "light";
  const rmCopy = REVENUE_MANAGEMENT_MESSAGES[languageId] || REVENUE_MANAGEMENT_MESSAGES.en;
  const demoCopy = REVENUE_DEMO_MESSAGES[languageId] || REVENUE_DEMO_MESSAGES.en;
  const sortLocale = languageId === "en" ? undefined : language?.locale || undefined;
  const bulkActionLabels = rmCopy.bulk.actionLabels;
  const bulkActionOptions = React.useMemo(
    () => BULK_ACTION_VALUES.map((value) => ({ value, label: bulkActionLabels[value] || value })),
    [bulkActionLabels]
  );
  const bulkStepTitles = rmCopy.bulk.stepTitles;

  const [rawRates, setRawRates] = useState([]);
  const [demoRatesLoading, setDemoRatesLoading] = useState(demoModeActive);
  const [demoRatesError, setDemoRatesError] = useState("");
  const rawRatesById = React.useMemo(() => {
    const index = new Map();
    (rawRates || []).forEach((rate) => {
      if (rate?.id != null) index.set(Number(rate.id), rate);
    });
    return index;
  }, [rawRates]);

  const getRateCode = (rate, fallbackId = null) => {
    if (rate?.rate_code) return rate.rate_code;
    if (rate?.id != null) return `#${rate.id}`;
    if (fallbackId != null) return `#${fallbackId}`;
    return null;
  };

  const getTwoLevelDependencyCodes = React.useCallback((derivedRate) => {
    const level1Id = Number(derivedRate?.base_rate_id || derivedRate?.base_rate?.id);
    if (!Number.isFinite(level1Id)) return [];

    const level1Rate = rawRatesById.get(level1Id);
    const level1Code =
      getRateCode(derivedRate?.base_rate) ||
      getRateCode(level1Rate, level1Id);

    const level2Id = Number(level1Rate?.base_rate_id || level1Rate?.base_rate?.id);
    if (!Number.isFinite(level2Id)) return [level1Code].filter(Boolean);

    const level2Rate = rawRatesById.get(level2Id);
    const level2Code =
      getRateCode(level1Rate?.base_rate) ||
      getRateCode(level2Rate, level2Id);

    return [level1Code, level2Code].filter(Boolean);
  }, [rawRatesById]);

  const [ratePrices, setRatePrices] = useState([]);
  const [quickSearchQuery, setQuickSearchQuery] = useState("");
  const [debouncedQuickSearchQuery, setDebouncedQuickSearchQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState(getDefaultRateColumnFilters);
  const [activeFilter, setActiveFilter] = useState(null); // { key, rect }
  const [filterOptionQuery, setFilterOptionQuery] = useState("");
  const [showNewRatePopup, setShowNewRatePopup] = useState(false);

  // 🆕 Bulk Apply UI
  const [showBulkApplyPopup, setShowBulkApplyPopup] = useState(false);
  const [bulkApplyStep, setBulkApplyStep] = useState(1);
  const [bulkApplySelectedRateIds, setBulkApplySelectedRateIds] = useState([]);
  const [bulkApplySearch, setBulkApplySearch] = useState("");
  const [bulkApplyAction, setBulkApplyAction] = useState("");
  const [bulkApplyStartDate, setBulkApplyStartDate] = useState("");
  const [bulkApplyEndDate, setBulkApplyEndDate] = useState("");
  const [bulkApplySubmitting, setBulkApplySubmitting] = useState(false);
  const [bulkBackgroundOperation, setBulkBackgroundOperation] = useState(null);
  const [bulkFailureReport, setBulkFailureReport] = useState(null);
  const bulkBackgroundDismissTimerRef = useRef(null);
  const [bulkStopSellReason, setBulkStopSellReason] = useState("");
  const [bulkRatePriceValue, setBulkRatePriceValue] = useState("");
  const [bulkRatePriceMinStay, setBulkRatePriceMinStay] = useState("");
  const [bulkRatePriceMaxStay, setBulkRatePriceMaxStay] = useState("");
  const [bulkMinStayValue, setBulkMinStayValue] = useState("");
  const [bulkMaxStayValue, setBulkMaxStayValue] = useState("");
  const bulkRateCurrencyContext = React.useMemo(
    () => resolveBulkRevenueRateCurrency(
      bulkApplySelectedRateIds,
      ratePrices,
      baseCurrency
    ),
    [baseCurrency, bulkApplySelectedRateIds, ratePrices]
  );
  const bulkRateCurrency = bulkRateCurrencyContext.currency;
  const bulkRateMinorUnits = getCurrencyMinorUnits(bulkRateCurrency, currencyCatalog);

  const [viewMode, setViewMode] = useState("rates"); // "rates" | "stop_sells"
  const [stopSellRows, setStopSellRows] = useState([]);
  const [stopSellLoading, setStopSellLoading] = useState(false);
  const pageViewportRef = useRef(null);
  const ratesTableWrapRef = useRef(null);
  const [ratesTableWrapHeight, setRatesTableWrapHeight] = useState(420);
  const filterPopoverRef = useRef(null);









  const [searchParams] = useSearchParams();
  const businessUnitId = demoModeActive ? DEMO_BUSINESS_UNIT_ID : searchParams.get("business_unit_id");

  

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuickSearchQuery(quickSearchQuery);
    }, 220);
    return () => clearTimeout(timer);
  }, [quickSearchQuery]);
// ⬇️ Define la función fuera del useEffect
const fetchRoomRates = async () => {
  if (demoModeActive) {
    setDemoRatesLoading(true);
    setDemoRatesError("");
  }
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/roomRates/summary`,
      { params: { business_unit_id: businessUnitId } }
    );

    console.log("[REV-MGMT][RoomRates][Summary][OK]", { count: data?.length });
    setRawRates(data || []);

    const grouped = Object.values(
      (data || []).reduce((acc, rate) => {
        const key = rate.group_key || `SINGLE-${rate.id}`;
        if (!acc[key]) {
          acc[key] = rate;
        } else {
          if (rate.id < acc[key].id) acc[key] = rate;
        }
        return acc;
      }, {})
    );

    setRatePrices(grouped);
  } catch (error) {
    if (demoModeActive) {
      setRawRates([]);
      setRatePrices([]);
      setDemoRatesError(error?.response?.data?.error || demoCopy.loadError);
    } else {
      console.error("[REV-MGMT][RoomRates][Summary][ERROR]", error);
    }
  } finally {
    if (demoModeActive) setDemoRatesLoading(false);
  }
};

const fetchStopSells = async () => {
  if (demoModeActive || isDemoMode()) return;
  setStopSellLoading(true);
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/stop-sells`,
      { params: { business_unit_id: businessUnitId } }
    );

    const all = Array.isArray(data) ? data : [];

    // Fallback defensivo: si el backend ignora params, filtramos por room_rate.business_unit_id si viene incluido
    const filtered = all.filter((ss) => {
      const ssBu = ss?.room_rate?.business_unit_id;
      if (ssBu == null) return true; // si el backend no incluye room_rate, no podemos filtrar aquí
      return String(ssBu) === String(businessUnitId);
    });

    setStopSellRows(filtered);

    console.log("[REV-MGMT][StopSells][Fetch][OK]", {
      business_unit_id: businessUnitId,
      count_total: all.length,
      count_filtered: filtered.length
    });
  } catch (error) {
    console.error("[REV-MGMT][StopSells][Fetch][ERROR]", { business_unit_id: businessUnitId, error });
    setStopSellRows([]);
  } finally {
    setStopSellLoading(false);
  }
};

const resetBulkApplyForm = () => {
  setBulkApplyStep(1);
  setBulkApplySelectedRateIds([]);
  setBulkApplySearch("");
  setBulkApplyAction("");
  setBulkApplyStartDate("");
  setBulkApplyEndDate("");
  setBulkStopSellReason("");
  setBulkRatePriceValue("");
  setBulkRatePriceMinStay("");
  setBulkRatePriceMaxStay("");
  setBulkMinStayValue("");
  setBulkMaxStayValue("");
  setBulkApplySubmitting(false);
};

const handleOpenBulkApplyPopup = () => {
  if (demoModeActive || isDemoMode()) return;
  console.log("[REV-MGMT][BulkApply][Open]");
  resetBulkApplyForm();
  setShowBulkApplyPopup(true);
};

const handleCloseBulkApplyPopup = () => {
  console.log("[REV-MGMT][BulkApply][Close]");
  setShowBulkApplyPopup(false);
  resetBulkApplyForm();
};

useEffect(() => {
  if (!pendingAction) return;
  if (demoModeActive) {
    if (typeof onPendingActionConsumed === "function") onPendingActionConsumed();
    return;
  }

  if (pendingAction === "bulk_apply") {
    console.log("[REV-MGMT][BulkApply][Open]");
    resetBulkApplyForm();
    setShowBulkApplyPopup(true);
  }

  if (pendingAction === "stop_sells_overview") {
    console.log("[REV-MGMT][ViewMode][Toggle]", { from: "matrix", to: "stop_sells" });
    setViewMode("stop_sells");
  }

  if (typeof onPendingActionConsumed === "function") {
    onPendingActionConsumed();
  }
}, [pendingAction, onPendingActionConsumed, demoModeActive]);

const parseOptionalInt = (rawValue) => {
  const raw = String(rawValue ?? "").trim();
  if (raw === "") return null;
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? NaN : parsed;
};

const handleBulkApplySubmit = async () => {
  if (demoModeActive || isDemoMode()) return;
  const selectedRateIds = (bulkApplySelectedRateIds || [])
    .map((id) => Number(id))
    .filter((id) => Number.isFinite(id));

  if (!selectedRateIds.length) {
    alert(rmCopy.alerts.selectRoomRate);
    return;
  }

  if (!bulkApplyAction) {
    alert(rmCopy.alerts.selectBulkAction);
    return;
  }

  if (bulkApplyAction === "upsert_rate_price") {
    const derivedSelected = selectedRateIds.filter((id) => {
      const rate = (ratePrices || []).find((r) => Number(r?.id) === id);
      return isDerivedRateForPriceBulk(rate);
    });
    if (derivedSelected.length) {
      alert(rmCopy.alerts.derivedRatePriceNotAllowed);
      return;
    }
  }

  const startDate = String(bulkApplyStartDate || "").slice(0, 10);
  const endDate = String(bulkApplyEndDate || "").slice(0, 10);

  if (!startDate || !endDate) {
    alert(rmCopy.alerts.selectDates);
    return;
  }
  if (endDate < startDate) {
    alert(rmCopy.alerts.endDateAfterStart);
    return;
  }

  let actionInputSummary = "";
  let requestBuilder = null;

  if (bulkApplyAction === "add_stop_sell") {
    const reason = String(bulkStopSellReason || "").trim();
    actionInputSummary = reason ? `${rmCopy.stopSells.headers.reason}: ${reason}` : `${rmCopy.stopSells.headers.reason}: (${rmCopy.values.none})`;
    requestBuilder = (roomRateId) => {
      const payload = {
        room_rate_id: roomRateId,
        start_date: startDate,
        end_date: endDate,
        is_automatic: false,
      };
      if (reason) payload.reason = reason;
      return {
        method: "post",
        url: `${process.env.REACT_APP_API_URL}/api/stop-sells`,
        payload,
      };
    };
  } else if (bulkApplyAction === "remove_stop_sell") {
    requestBuilder = (roomRateId) => ({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/api/stop-sells/delete-range`,
      payload: {
        room_rate_id: roomRateId,
        start_date: startDate,
        end_date: endDate,
      },
    });
  } else if (bulkApplyAction === "upsert_rate_price") {
    const bulkCurrencyContext = resolveBulkRevenueRateCurrency(
      selectedRateIds,
      ratePrices,
      baseCurrency
    );
    if (bulkCurrencyContext.mixed) {
      alert(rmCopy.alerts.mixedRateCurrencies);
      return;
    }
    const priceCurrency = bulkCurrencyContext.currency;
    const priceMinorUnits = getCurrencyMinorUnits(priceCurrency, currencyCatalog);
    const price = Number(String(bulkRatePriceValue || "").trim().replace(",", "."));
    if (!Number.isFinite(price)) {
      alert(rmCopy.alerts.validPrice);
      return;
    }
    if (!isValidRevenueCurrencyInput(bulkRatePriceValue, priceCurrency, currencyCatalog)) {
      alert(rmCopy.alerts.validPricePrecision(priceCurrency, priceMinorUnits));
      return;
    }

    const minStay = parseOptionalInt(bulkRatePriceMinStay);
    const maxStay = parseOptionalInt(bulkRatePriceMaxStay);

    if (Number.isNaN(minStay) || Number.isNaN(maxStay)) {
      alert(rmCopy.alerts.validMinMaxIntegers);
      return;
    }
    if (minStay != null && minStay < 1) {
      alert(rmCopy.alerts.optionalMinStayMin);
      return;
    }
    if (maxStay != null && maxStay < 1) {
      alert(rmCopy.alerts.optionalMaxStayMin);
      return;
    }
    if (minStay != null && maxStay != null && maxStay < minStay) {
      alert(rmCopy.alerts.optionalMaxStayBelowMin);
      return;
    }

    actionInputSummary = [
      `${rmCopy.bulk.price}: ${price}`,
      minStay != null ? `${rmCopy.bulk.minStay}: ${minStay}` : null,
      maxStay != null ? `${rmCopy.bulk.maxStay}: ${maxStay}` : null,
    ]
      .filter(Boolean)
      .join(" | ");

    requestBuilder = (roomRateId) => {
      const payload = {
        room_rate_id: roomRateId,
        start_date: startDate,
        end_date: endDate,
        price,
      };
      if (minStay != null) payload.min_stay = minStay;
      if (maxStay != null) payload.max_stay = maxStay;
      return {
        method: "post",
        url: `${process.env.REACT_APP_API_URL}/api/rate-prices`,
        payload,
      };
    };
  } else if (bulkApplyAction === "set_min_stay") {
    const minStay = parseOptionalInt(bulkMinStayValue);
    if (!Number.isInteger(minStay) || minStay < 1) {
      alert(rmCopy.alerts.validMinStay);
      return;
    }
    actionInputSummary = `${rmCopy.bulk.minStay}: ${minStay}`;
    requestBuilder = (roomRateId) => ({
      method: "patch",
      url: `${process.env.REACT_APP_API_URL}/api/rate-prices/set-min-stay`,
      payload: {
        room_rate_id: roomRateId,
        start_date: startDate,
        end_date: endDate,
        min_stay: minStay,
      },
    });
  } else if (bulkApplyAction === "set_max_stay") {
    const maxStay = parseOptionalInt(bulkMaxStayValue);
    if (!Number.isInteger(maxStay) || maxStay < 1) {
      alert(rmCopy.alerts.validMaxStay);
      return;
    }
    actionInputSummary = `${rmCopy.bulk.maxStay}: ${maxStay}`;
    requestBuilder = (roomRateId) => ({
      method: "patch",
      url: `${process.env.REACT_APP_API_URL}/api/rate-prices/set-max-stay`,
      payload: {
        room_rate_id: roomRateId,
        start_date: startDate,
        end_date: endDate,
        max_stay: maxStay,
      },
    });
  }

  if (!requestBuilder) {
    alert(rmCopy.alerts.bulkActionNotConfigured);
    return;
  }

  const actionLabel = bulkActionLabels[bulkApplyAction] || bulkApplyAction;
  const confirmMessage = [
    `${rmCopy.confirm.action}: ${actionLabel}`,
    `${rmCopy.confirm.ratesSelected}: ${selectedRateIds.length}`,
    `${rmCopy.confirm.dateRange}: ${startDate} -> ${endDate}`,
    actionInputSummary ? `${rmCopy.confirm.inputs}: ${actionInputSummary}` : null,
    "",
    rmCopy.confirm.proceed,
  ]
    .filter(Boolean)
    .join("\n");

  const confirmed = window.confirm(confirmMessage);
  if (!confirmed) return;

  console.log("[REV-MGMT][BULK][ACTION]", {
    phase: "START",
    action: bulkApplyAction,
    rates_count: selectedRateIds.length,
    start_date: startDate,
    end_date: endDate,
  });

  const buildTargetDays = () => {
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    const out = [];

    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      out.push(`${y}-${m}-${day}`);
    }

    return out;
  };

  const targetDays = buildTargetDays();
  const targetDaySet = new Set(targetDays);

  const getEntityDay = (row) => String(row?.stay_date || row?.start_date || "").slice(0, 10);

  const valueMatches = (actual, expected) => {
    if (typeof expected === "undefined") return true;
    if (expected === null) return actual == null;

    const actualNumber = Number(actual);
    const expectedNumber = Number(expected);
    if (!Number.isFinite(actualNumber) || !Number.isFinite(expectedNumber)) return false;

    return actualNumber === expectedNumber;
  };

  const priceMatches = (actual, expected) => {
    if (typeof expected === "undefined") return true;

    const actualNumber = Number(actual);
    const expectedNumber = Number(expected);
    if (!Number.isFinite(actualNumber) || !Number.isFinite(expectedNumber)) return false;

    return Math.abs(actualNumber - expectedNumber) < 0.01;
  };

  const verifyBulkRequestApplied = async (roomRateId, req) => {
    if (demoModeActive || isDemoMode()) return false;
    if (bulkApplyAction === "add_stop_sell" || bulkApplyAction === "remove_stop_sell") {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/stop-sells`,
        {
          params: { business_unit_id: businessUnitId },
          withCredentials: true,
        }
      );

      const stopSells = Array.isArray(data) ? data : [];
      const matchedDays = new Set(
        stopSells
          .filter((row) => String(row.room_rate_id) === String(roomRateId))
          .map((row) => getEntityDay(row))
          .filter((day) => targetDaySet.has(day))
      );

      return bulkApplyAction === "add_stop_sell"
        ? matchedDays.size === targetDays.length
        : matchedDays.size === 0;
    }

    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/rate-prices/history/${roomRateId}`,
      { withCredentials: true }
    );

    const historyRows = Array.isArray(data) ? data : [];
    const rowsByDay = new Map(
      historyRows
        .filter((row) => String(row.room_rate_id) === String(roomRateId))
        .map((row) => [getEntityDay(row), row])
    );

    return targetDays.every((day) => {
      const row = rowsByDay.get(day);
      if (!row) return false;

      if (bulkApplyAction === "upsert_rate_price") {
        return (
          priceMatches(row.price, req.payload?.price) &&
          valueMatches(row.min_stay, req.payload?.min_stay) &&
          valueMatches(row.max_stay, req.payload?.max_stay)
        );
      }

      if (bulkApplyAction === "set_min_stay") {
        return valueMatches(row.min_stay, req.payload?.min_stay);
      }

      if (bulkApplyAction === "set_max_stay") {
        return valueMatches(row.max_stay, req.payload?.max_stay);
      }

      return false;
    });
  };

  const operationId = Date.now() + "-" + Math.random().toString(36).slice(2, 9);
  const requests = selectedRateIds.map((roomRateId) => ({
    roomRateId,
    request: requestBuilder(roomRateId),
  }));
  const waitForVerification = (milliseconds) =>
    new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  const isAmbiguousRequestError = (requestError) => {
    const status = Number(requestError?.response?.status || 0);
    return (
      !requestError?.response ||
      requestError?.code === "ECONNABORTED" ||
      status === 502 ||
      status === 503 ||
      status === 504
    );
  };
  const verifyUntilConfirmed = async (roomRateId, request) => {
    const deadline = Date.now() + 6 * 60 * 1000;
    let delayMs = 2500;

    while (Date.now() < deadline) {
      if (demoModeActive || isDemoMode()) return false;
      await waitForVerification(delayMs);
      if (isDemoMode()) return false;

      try {
        if (await verifyBulkRequestApplied(roomRateId, request)) {
          return true;
        }
      } catch (verificationError) {
        console.warn("[REV-MGMT][BULK][ACTION]", {
          phase: "VERIFY_RETRY",
          action: bulkApplyAction,
          room_rate_id: roomRateId,
          error: verificationError?.message || verificationError,
        });
      }

      delayMs = Math.min(15000, Math.round(delayMs * 1.6));
    }

    return false;
  };
  const updateBackgroundOperation = (patch) => {
    setBulkBackgroundOperation((current) =>
      current?.id === operationId ? { ...current, ...patch } : current
    );
  };

  if (bulkBackgroundDismissTimerRef.current) {
    window.clearTimeout(bulkBackgroundDismissTimerRef.current);
    bulkBackgroundDismissTimerRef.current = null;
  }

  setBulkApplySubmitting(true);
  setBulkFailureReport(null);
  setBulkBackgroundOperation({
    id: operationId,
    phase: "queued",
    action: bulkApplyAction,
    actionLabel,
    total: requests.length,
    completed: 0,
    succeeded: 0,
    failed: 0,
    detail: rmCopy.bulk.backgroundStarted(actionLabel, requests.length),
  });

  // The wizard closes immediately. The detached task keeps processing and only
  // reports a failure after an ambiguous gateway response has been verified.
  setShowBulkApplyPopup(false);
  resetBulkApplyForm();

  Promise.resolve().then(async () => {
    if (demoModeActive || isDemoMode()) return;
    let successCount = 0;
    let completedCount = 0;
    const failures = [];

    for (const { roomRateId, request } of requests) {
      if (isDemoMode()) return;
      updateBackgroundOperation({
        phase: "running",
        detail: rmCopy.bulk.backgroundProgress(completedCount, requests.length),
      });

      try {
        console.log("[REV-MGMT][BULK][ACTION]", {
          phase: "REQUEST",
          action: bulkApplyAction,
          room_rate_id: roomRateId,
          payload: request.payload,
        });

        let response;
        if (request.method === "patch") {
          response = await axios.patch(request.url, request.payload);
        } else if (request.method === "put") {
          response = await axios.put(request.url, request.payload);
        } else {
          response = await axios.post(request.url, request.payload);
        }

        successCount += 1;
        console.log("[REV-MGMT][BULK][ACTION]", {
          phase: "OK",
          action: bulkApplyAction,
          room_rate_id: roomRateId,
          result: response?.data,
        });
      } catch (requestError) {
        if (isDemoMode()) return;
        let appliedAfterAmbiguousResponse = false;

        if (isAmbiguousRequestError(requestError)) {
          updateBackgroundOperation({
            phase: "verifying",
            detail: rmCopy.bulk.backgroundVerifying,
          });
          appliedAfterAmbiguousResponse = await verifyUntilConfirmed(roomRateId, request);
        }

        if (appliedAfterAmbiguousResponse) {
          successCount += 1;
          console.log("[REV-MGMT][BULK][ACTION]", {
            phase: "OK_AFTER_VERIFICATION",
            action: bulkApplyAction,
            room_rate_id: roomRateId,
            payload: request.payload,
          });
        } else {
          failures.push({
            room_rate_id: roomRateId,
            error: isAmbiguousRequestError(requestError)
              ? rmCopy.bulk.backgroundUnconfirmed
              : requestError?.response?.data?.error ||
                requestError?.message ||
                rmCopy.values.unknownError,
          });
          console.error("[REV-MGMT][BULK][ACTION]", {
            phase: "CONFIRMED_ERROR",
            action: bulkApplyAction,
            room_rate_id: roomRateId,
            error: requestError,
          });
        }
      }

      completedCount += 1;
      updateBackgroundOperation({
        phase: "running",
        completed: completedCount,
        succeeded: successCount,
        failed: failures.length,
        detail: rmCopy.bulk.backgroundProgress(completedCount, requests.length),
      });
    }

    if (isDemoMode()) return;
    if (successCount > 0) {
      if (
        viewMode === "stop_sells" ||
        bulkApplyAction === "add_stop_sell" ||
        bulkApplyAction === "remove_stop_sell"
      ) {
        await Promise.all([fetchRoomRates(), fetchStopSells()]);
      } else {
        await fetchRoomRates();
      }
    }

    if (failures.length > 0) {
      const report = {
        id: operationId,
        actionLabel,
        startDate,
        endDate,
        total: requests.length,
        succeeded: successCount,
        failures,
      };
      setBulkFailureReport(report);
      updateBackgroundOperation({
        phase: "failed",
        completed: requests.length,
        succeeded: successCount,
        failed: failures.length,
        detail: rmCopy.bulk.backgroundFailed(failures.length, requests.length),
      });
      return;
    }

    updateBackgroundOperation({
      phase: "success",
      completed: requests.length,
      succeeded: successCount,
      failed: 0,
      detail: rmCopy.bulk.backgroundComplete(successCount),
    });
    bulkBackgroundDismissTimerRef.current = window.setTimeout(() => {
      setBulkBackgroundOperation((current) =>
        current?.id === operationId ? null : current
      );
      bulkBackgroundDismissTimerRef.current = null;
    }, 8000);
  }).catch((unexpectedError) => {
    const failure = {
      room_rate_id: null,
      error:
        unexpectedError?.response?.data?.error ||
        unexpectedError?.message ||
        rmCopy.values.unknownError,
    };

    console.error("[REV-MGMT][BULK][ACTION]", {
      phase: "BACKGROUND_TASK_ERROR",
      action: bulkApplyAction,
      error: unexpectedError,
    });

    setBulkFailureReport({
      id: operationId,
      actionLabel,
      startDate,
      endDate,
      total: requests.length,
      succeeded: 0,
      failures: [failure],
    });
    updateBackgroundOperation({
      phase: "failed",
      completed: 0,
      succeeded: 0,
      failed: 1,
      detail: rmCopy.bulk.backgroundFailed(1, requests.length),
    });
  });
};

useEffect(() => {
  if (!showBulkApplyPopup) return;
  if (bulkApplyAction !== "upsert_rate_price") return;

  setBulkApplySelectedRateIds((prev) => {
    const next = (prev || []).filter((id) => {
      const rate = (ratePrices || []).find((r) => Number(r?.id) === Number(id));
      return !isDerivedRateForPriceBulk(rate);
    });
    if (next.length !== (prev || []).length) {
      console.log("[REV-MGMT][BULK][ACTION]", {
        phase: "SELECTION_PRUNED",
        action: bulkApplyAction,
        from: (prev || []).length,
        to: next.length,
      });
    }
    return next;
  });
}, [bulkApplyAction, showBulkApplyPopup, ratePrices]);

const isBulkApplyDateRangeValid = () =>
  !!bulkApplyStartDate && !!bulkApplyEndDate && bulkApplyEndDate >= bulkApplyStartDate;

const isBulkApplyStep4Ready = () => {
  if (bulkApplyAction === "add_stop_sell" || bulkApplyAction === "remove_stop_sell") return true;
  if (bulkApplyAction === "upsert_rate_price") return String(bulkRatePriceValue || "").trim() !== "";
  if (bulkApplyAction === "set_min_stay") return String(bulkMinStayValue || "").trim() !== "";
  if (bulkApplyAction === "set_max_stay") return String(bulkMaxStayValue || "").trim() !== "";
  return false;
};

const handleBulkApplyNextStep = () => {
  if (demoModeActive || isDemoMode()) return;
  if (bulkApplyStep === 1) {
    if (!bulkApplyAction) {
      alert(rmCopy.alerts.selectBulkAction);
      return;
    }
    setBulkApplyStep(2);
    return;
  }

  if (bulkApplyStep === 2) {
    if ((bulkApplySelectedRateIds || []).length === 0) {
      alert(rmCopy.alerts.selectRoomRate);
      return;
    }
    setBulkApplyStep(3);
    return;
  }

  if (bulkApplyStep === 3) {
    if (!isBulkApplyDateRangeValid()) {
      alert(rmCopy.alerts.bulkDateRangeValid);
      return;
    }
    setBulkApplyStep(4);
  }
};




// 🔹 Efecto inicial
useEffect(() => {
  if (businessUnitId) fetchRoomRates();
}, [businessUnitId]);

useEffect(() => {
  if (demoModeActive || viewMode !== "stop_sells") return;
  fetchStopSells();
}, [viewMode, businessUnitId, demoModeActive]);

useEffect(() => {
  if (viewMode !== "rates") return undefined;

  const recalcTableHeight = () => {
    const pageViewport = pageViewportRef.current;
    let viewportBottom = window.innerHeight;

    if (pageViewport) {
      const pageRect = pageViewport.getBoundingClientRect();
      if (pageRect.height > 0) {
        viewportBottom = pageRect.bottom;
      }
    }

    const container = ratesTableWrapRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const bottomGap = window.innerWidth <= 992 ? 8 : 10;
    const minVisibleHeight = window.innerWidth <= 992 ? 220 : 280;
    const availableHeight = Math.floor(viewportBottom - rect.top - bottomGap);
    const nextHeight = Math.max(minVisibleHeight, availableHeight);

    setRatesTableWrapHeight((prev) => (Math.abs(prev - nextHeight) > 1 ? nextHeight : prev));
  };

  const frameId = window.requestAnimationFrame(recalcTableHeight);
  window.addEventListener("resize", recalcTableHeight);
  return () => {
    window.cancelAnimationFrame(frameId);
    window.removeEventListener("resize", recalcTableHeight);
  };
}, [viewMode, ratePrices.length, debouncedQuickSearchQuery, columnFilters]);

  const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "32px",
    // Quitar altura fija para permitir crecimiento dinámico
    // height: "32px", ❌ eliminamos esta línea
    fontSize: "0.85rem",
    backgroundColor: "#172653",
    borderColor: state.isFocused ? "#3db6e9" : "rgba(99, 193, 199, 0.32)",
    boxShadow: "none",
    color: "#fff",
    borderRadius: "4px",
    padding: "0 4px",
    flexWrap: "wrap" // ✅ clave para que los valores salten a nueva línea
  }),

  menu: (provided) => ({
    ...provided,
    backgroundColor: "#101a36",
    fontSize: "0.85rem",
    zIndex: 10001
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? "#172653" : "#101a36",
    color: "#fff",
    fontSize: "0.85rem",
    cursor: "pointer"
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: "#223a7a",
    fontSize: "0.85rem"
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: "#fff",
    fontSize: "0.85rem"
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: "#b2c1d9",
    ":hover": {
      backgroundColor: "#223a7a",
      color: "#fff"
    }
  }),
  input: (styles) => ({
    ...styles,
    color: "#fff"
  }),
  placeholder: (styles) => ({
    ...styles,
    color: "#b2c1d9",
    fontSize: "0.85rem"
  }),
  singleValue: (styles) => ({
    ...styles,
    color: "#fff",
    fontSize: "0.85rem",
    opacity: 1 // 🧽 Limpia la opacidad grisácea por defecto
  })
};

const getDerivedRateSummary = React.useCallback((rate) => {
  if (rate?.has_formula_link && rate?.formula_inputs?.length) {
    const abbrevMap = {
      average: "AVG",
      weighted: "WGT",
      min: "MIN",
      max: "MAX",
      custom: "CST",
    };
    const shortType = abbrevMap[rate.formula_type] || "CST";
    const baseCodes = rate.formula_inputs.map((f) => f.rate_code || "?").join(" + ");
    return `${shortType} ${baseCodes}`.trim();
  }

  if (rate?.adjustment_value != null && rate?.adjustment_type) {
    const numericAdjustment = Number.parseFloat(rate.adjustment_value);
    if (!Number.isFinite(numericAdjustment)) return "-";
    const arrow = numericAdjustment > 0 ? "UP" : numericAdjustment < 0 ? "DOWN" : "FLAT";
    const dependencyCodes = getTwoLevelDependencyCodes(rate);
    const suffix = dependencyCodes.length ? ` ${dependencyCodes.join(" -> ")}` : "";
    return `${arrow} ${formatRevenueAdjustmentValue(
      numericAdjustment,
      rate.adjustment_type,
      resolveRevenueRateCurrency(rate, baseCurrency),
      currencyCatalog
    )}${suffix}`;
  }

  return "-";
}, [baseCurrency, currencyCatalog, getTwoLevelDependencyCodes]);

const rateRows = React.useMemo(() => {
  return (ratePrices || []).map((rate) => {
    const roomRateLabel = `${rate?.name || rmCopy.values.notAvailable} (${rate?.rate_code || rmCopy.values.notAvailable})`;
    const derivedRatesLabel = getDerivedRateSummary(rate);
    const clientLabel = rate?.client?.name || rmCopy.values.noClient;
    const roomCategoryLabels = Array.from(
      new Set(
        [
          ...(rate?.sister_categories || []).map((c) => c?.name),
          rate?.room_category?.name,
        ]
          .map((v) => String(v || "").trim())
          .filter(Boolean)
      )
    );
    if (!roomCategoryLabels.length) roomCategoryLabels.push(rmCopy.values.notAvailable);

    const statusLabel = formatRateStatus(rate?.status, rmCopy);
    const adultsLabel = formatMatchValue(rate?.adults, rate?.adults_match_type, rmCopy.values.any, rmCopy);
    const maxOccupancyLabel = formatMatchValue(
      rate?.max_occupancy,
      rate?.max_occupancy_match_type,
      rmCopy.values.any,
      rmCopy
    );
    const childrenLabel = formatMatchValue(rate?.children, rate?.children_match_type, rmCopy.values.any, rmCopy);
    const showInBookingEngineLabel = rate?.show_in_booking_engine ? rmCopy.values.yes : rmCopy.values.no;
    const refundableLabel = rate?.is_refundable ? rmCopy.values.yes : rmCopy.values.no;
    const penaltyLabel = rate?.penalty_type || rmCopy.values.none;
    const rateCurrency = resolveRevenueRateCurrency(rate, baseCurrency);
    const minPriceLabel = formatRevenueCurrencyValue(rate?.min_price_amount, rateCurrency, currencyCatalog);
    const maxPriceLabel = formatRevenueCurrencyValue(rate?.max_price_amount, rateCurrency, currencyCatalog);
    const defaultMinStayLabel = rate?.default_min_stay != null ? String(rate.default_min_stay) : "-";
    const defaultMaxStayLabel = rate?.default_max_stay != null ? String(rate.default_max_stay) : "-";
    const startDateLabel = rate?.valid_from?.substring(0, 10) || "-";
    const endDateLabel = rate?.valid_to?.substring(0, 10) || "-";

    const searchIndex = [
      roomRateLabel,
      derivedRatesLabel,
      clientLabel,
      roomCategoryLabels.join(" + "),
      statusLabel,
      adultsLabel,
      maxOccupancyLabel,
      childrenLabel,
      showInBookingEngineLabel,
      refundableLabel,
      penaltyLabel,
      minPriceLabel,
      maxPriceLabel,
      defaultMinStayLabel,
      defaultMaxStayLabel,
      startDateLabel,
      endDateLabel,
    ]
      .join(" ")
      .toLowerCase();

    return {
      rate,
      roomCategoryText: roomCategoryLabels.join(" + "),
      searchIndex,
      filterValues: {
        roomRate: roomRateLabel,
        derivedRates: derivedRatesLabel,
        client: clientLabel,
        roomCategory: roomCategoryLabels,
        status: statusLabel,
        adults: adultsLabel,
        maxOccupancy: maxOccupancyLabel,
        children: childrenLabel,
        showInBookingEngine: showInBookingEngineLabel,
        refundable: refundableLabel,
        penalty: penaltyLabel,
        minPrice: minPriceLabel,
        maxPrice: maxPriceLabel,
        defaultMinStay: defaultMinStayLabel,
        defaultMaxStay: defaultMaxStayLabel,
        startDate: startDateLabel,
        endDate: endDateLabel,
      },
    };
  });
}, [baseCurrency, currencyCatalog, ratePrices, getDerivedRateSummary, rmCopy]);

const setFilterStats = React.useMemo(() => {
  const sortByLabel = (a, b) => a.label.localeCompare(b.label, sortLocale, { sensitivity: "base", numeric: true });

  const buildSetStats = (extractValues) => {
    const counts = new Map();

    rateRows.forEach((row) => {
      const raw = extractValues(row);
      const values = Array.isArray(raw) ? raw : [raw];
      const uniqueValues = Array.from(
        new Set(
          values
            .map((v) => String(v ?? "-").trim())
            .filter(Boolean)
        )
      );

      uniqueValues.forEach((value) => {
        counts.set(value, (counts.get(value) || 0) + 1);
      });
    });

    const options = Array.from(counts.entries())
      .map(([value, count]) => ({
        value,
        label: value,
        count,
      }))
      .sort(sortByLabel);

    return {
      options,
      labels: new Map(options.map((opt) => [String(opt.value), String(opt.label)])),
    };
  };

  return {
    roomRate: buildSetStats((row) => row.filterValues.roomRate),
    derivedRates: buildSetStats((row) => row.filterValues.derivedRates),
    client: buildSetStats((row) => row.filterValues.client),
    roomCategory: buildSetStats((row) => row.filterValues.roomCategory),
    status: buildSetStats((row) => row.filterValues.status),
    adults: buildSetStats((row) => row.filterValues.adults),
    maxOccupancy: buildSetStats((row) => row.filterValues.maxOccupancy),
    children: buildSetStats((row) => row.filterValues.children),
    showInBookingEngine: buildSetStats((row) => row.filterValues.showInBookingEngine),
    refundable: buildSetStats((row) => row.filterValues.refundable),
    penalty: buildSetStats((row) => row.filterValues.penalty),
    minPrice: buildSetStats((row) => row.filterValues.minPrice),
    maxPrice: buildSetStats((row) => row.filterValues.maxPrice),
    defaultMinStay: buildSetStats((row) => row.filterValues.defaultMinStay),
    defaultMaxStay: buildSetStats((row) => row.filterValues.defaultMaxStay),
    startDate: buildSetStats((row) => row.filterValues.startDate),
    endDate: buildSetStats((row) => row.filterValues.endDate),
  };
}, [rateRows, sortLocale]);

const rateFilterTitles = React.useMemo(
  () => ({
    roomRate: rmCopy.rates.headers.roomRate,
    derivedRates: rmCopy.rates.headers.derivedRates,
    client: rmCopy.rates.headers.client,
    roomCategory: rmCopy.rates.headers.roomCategory,
    status: rmCopy.rates.headers.status,
    adults: rmCopy.rates.headers.adults,
    maxOccupancy: rmCopy.rates.headers.maxOccupancy,
    children: rmCopy.rates.headers.children,
    showInBookingEngine: rmCopy.rates.headers.showInBookingEngine,
    refundable: rmCopy.rates.headers.refundable,
    penalty: rmCopy.rates.headers.penalty,
    minPrice: rmCopy.rates.headers.minPrice,
    maxPrice: rmCopy.rates.headers.maxPrice,
    defaultMinStay: rmCopy.rates.headers.defaultMinStay,
    defaultMaxStay: rmCopy.rates.headers.defaultMaxStay,
    startDate: rmCopy.rates.headers.startDate,
    endDate: rmCopy.rates.headers.endDate,
  }),
  [rmCopy]
);

const closeFilterPopover = React.useCallback(() => setActiveFilter(null), []);

const toggleFilterPopover = (key, triggerElement) => {
  if (!key) return;
  setActiveFilter((prev) => {
    if (prev?.key === key) return null;
    return { key, rect: triggerElement?.getBoundingClientRect?.() || null };
  });
};

const columnIsFiltered = (key) => {
  const value = columnFilters[key];
  return Array.isArray(value) ? value.length > 0 : String(value || "").trim().length > 0;
};

const clearColumnFilter = (key) => {
  setColumnFilters((prev) => ({
    ...prev,
    [key]: Array.isArray(prev[key]) ? [] : "",
  }));
};

const toggleMultiSelectFilter = (key, value) => {
  setColumnFilters((prev) => {
    const prevArr = Array.isArray(prev[key]) ? prev[key] : [];
    const exists = prevArr.includes(value);
    const nextArr = exists ? prevArr.filter((v) => v !== value) : [...prevArr, value];
    return { ...prev, [key]: nextArr };
  });
};

const columnSubtitle = (key) => {
  const value = columnFilters[key];
  if (!Array.isArray(value) || !value.length) return rmCopy.values.all;
  if (value.length === 1) {
    return setFilterStats[key]?.labels?.get(String(value[0])) ?? String(value[0]);
  }
  return rmCopy.filters.selected(value.length);
};

const filterModel = React.useMemo(() => {
  const key = activeFilter?.key;
  if (!key) return null;
  const title = rateFilterTitles[key];
  if (!title) return null;
  return {
    key,
    title,
    type: "set",
    options: setFilterStats[key]?.options || [],
  };
}, [activeFilter?.key, rateFilterTitles, setFilterStats]);

const rateTableColumns = React.useMemo(
  () => [
    ...(permissions["revenue.view_price_detail"]
      ? [{ key: "roomRate", label: rmCopy.rates.headers.roomRate, sticky: "rm-sticky-1" }]
      : []),
    {
      key: "derivedRates",
      label: rmCopy.rates.headers.derivedRates,
      sticky: permissions["revenue.view_price_detail"] ? "rm-sticky-2" : "",
    },
    { key: "client", label: rmCopy.rates.headers.client, sticky: "" },
    { key: "roomCategory", label: rmCopy.rates.headers.roomCategory, sticky: "" },
    { key: "status", label: rmCopy.rates.headers.status, sticky: "" },
    { key: "adults", label: rmCopy.rates.headers.adults, sticky: "" },
    { key: "maxOccupancy", label: rmCopy.rates.headers.maxOccupancy, sticky: "" },
    { key: "children", label: rmCopy.rates.headers.children, sticky: "" },
    { key: "showInBookingEngine", label: rmCopy.rates.headers.showInBookingEngine, sticky: "" },
    { key: "refundable", label: rmCopy.rates.headers.refundable, sticky: "" },
    { key: "penalty", label: rmCopy.rates.headers.penalty, sticky: "" },
    { key: "minPrice", label: rmCopy.rates.headers.minPrice, sticky: "" },
    { key: "maxPrice", label: rmCopy.rates.headers.maxPrice, sticky: "" },
    { key: "defaultMinStay", label: rmCopy.rates.headers.defaultMinStay, sticky: "" },
    { key: "defaultMaxStay", label: rmCopy.rates.headers.defaultMaxStay, sticky: "" },
    { key: "startDate", label: rmCopy.rates.headers.startDate, sticky: "" },
    { key: "endDate", label: rmCopy.rates.headers.endDate, sticky: "" },
  ],
  [permissions, rmCopy]
);

const popupTheme = React.useMemo(
  () => ({
    newRateOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: isLightTheme ? "rgba(23, 38, 83, 0.42)" : "rgba(5, 12, 32, 0.76)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999,
    },
    newRateModal: {
      backgroundColor: isLightTheme ? "#ffffff" : "#101a36",
      border: isLightTheme ? "1px solid #d7e2ec" : "1px solid rgba(61, 182, 233, 0.36)",
      boxShadow: isLightTheme ? "0 24px 58px rgba(34, 58, 122, 0.18)" : "0 26px 60px rgba(0, 0, 0, 0.4)",
      padding: "24px",
      borderRadius: "12px",
      width: "750px",
      maxHeight: "90vh",
      overflowY: "auto",
    },
    overlay: {
      position: "fixed",
      inset: 0,
      zIndex: 10000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: isLightTheme ? "rgba(23, 38, 83, 0.42)" : "rgba(5, 12, 32, 0.8)",
      backdropFilter: "blur(4px)",
      WebkitBackdropFilter: "blur(4px)",
      padding: "16px",
    },
    modal: {
      width: "min(1020px, 96vw)",
      maxHeight: "94vh",
      overflowY: "auto",
      borderRadius: "18px",
      border: isLightTheme ? "1px solid #d7e2ec" : "1px solid rgba(61, 182, 233, 0.36)",
      background: isLightTheme
        ? "#ffffff"
        : "linear-gradient(180deg, rgba(16, 26, 54, 0.98), rgba(10, 18, 41, 0.98))",
      boxShadow: isLightTheme ? "0 24px 58px rgba(34, 58, 122, 0.18)" : "0 26px 70px rgba(0, 0, 0, 0.52)",
      color: isLightTheme ? "#26354f" : "rgba(235, 241, 252, 0.94)",
      padding: "16px",
    },
    title: {
      fontSize: "1.2rem",
      fontWeight: 900,
      color: isLightTheme ? "#172653" : "rgba(244, 248, 255, 0.95)",
    },
    subtitle: {
      marginTop: "4px",
      fontSize: "0.84rem",
      color: isLightTheme ? "#5b6680" : "rgba(210, 222, 245, 0.72)",
    },
    section: {
      border: isLightTheme ? "1px solid #d7e2ec" : "1px solid rgba(99, 193, 199, 0.2)",
      borderRadius: "12px",
      padding: "12px",
      background: isLightTheme ? "#f7fafc" : "rgba(255, 255, 255, 0.035)",
    },
    sectionTitle: {
      color: isLightTheme ? "#172653" : "#dfe9fa",
      fontSize: "0.86rem",
      fontWeight: 700,
      marginBottom: "8px",
    },
    label: {
      color: isLightTheme ? "#26354f" : "#d3def1",
      fontSize: "0.85rem",
      marginBottom: "4px",
      display: "block",
    },
    input: {
      width: "100%",
      background: isLightTheme ? "#ffffff" : "rgba(255, 255, 255, 0.05)",
      border: isLightTheme ? "1px solid #bdcad8" : "1px solid rgba(99, 193, 199, 0.22)",
      color: isLightTheme ? "#26354f" : "rgba(239, 245, 255, 0.95)",
      padding: "8px 10px",
      borderRadius: "10px",
      fontSize: "0.9rem",
    },
    helperText: {
      marginTop: "9px",
      fontSize: "0.84rem",
      color: isLightTheme ? "#5b6680" : "rgba(210, 222, 245, 0.72)",
    },
    searchRowInput: {
      flex: "1 1 320px",
      background: isLightTheme ? "#ffffff" : "rgba(255, 255, 255, 0.05)",
      border: isLightTheme ? "1px solid #bdcad8" : "1px solid rgba(99, 193, 199, 0.22)",
      color: isLightTheme ? "#26354f" : "rgba(239, 245, 255, 0.95)",
      padding: "8px 10px",
      borderRadius: "10px",
      fontSize: "0.9rem",
    },
    listShell: {
      border: isLightTheme ? "1px solid #d7e2ec" : "1px solid rgba(99, 193, 199, 0.2)",
      borderRadius: "12px",
      overflow: "hidden",
      background: isLightTheme ? "#ffffff" : "rgba(255, 255, 255, 0.02)",
    },
    listHeader: {
      display: "grid",
      gridTemplateColumns: "44px 1fr 170px 180px",
      gap: "0px",
      padding: "10px 12px",
      background: isLightTheme
        ? "#edf5fa"
        : "linear-gradient(180deg, rgba(23, 38, 83, 0.98), rgba(16, 26, 54, 0.98))",
      borderBottom: isLightTheme ? "1px solid #d7e2ec" : "1px solid rgba(99, 193, 199, 0.18)",
      color: isLightTheme ? "#223a7a" : "rgba(214, 227, 247, 0.88)",
      fontSize: "0.8rem",
      fontWeight: 700,
    },
    listRow: {
      display: "grid",
      gridTemplateColumns: "44px 1fr 170px 180px",
      padding: "10px 12px",
      borderBottom: isLightTheme ? "1px solid #d7e2ec" : "1px solid rgba(99, 193, 199, 0.14)",
      background: isLightTheme ? "#ffffff" : "rgba(255, 255, 255, 0.015)",
      alignItems: "center",
      color: isLightTheme ? "#26354f" : "rgba(233, 240, 252, 0.9)",
      fontSize: "0.9rem",
    },
    listSubtleText: {
      color: isLightTheme ? "#5b6680" : "rgba(214, 227, 247, 0.86)",
    },
    warning: {
      padding: "9px 12px",
      color: isLightTheme ? "#28697e" : "#9edff1",
      backgroundColor: isLightTheme ? "#edf8fc" : "rgba(61, 182, 233, 0.1)",
      borderBottom: isLightTheme ? "1px solid #add7e5" : "1px solid rgba(61, 182, 233, 0.28)",
      borderTop: isLightTheme ? "1px solid #add7e5" : "1px solid rgba(61, 182, 233, 0.28)",
      fontSize: "0.82rem",
      fontWeight: 700,
    },
    empty: {
      padding: "12px",
      color: isLightTheme ? "#5b6680" : "rgba(224, 236, 255, 0.84)",
      background: isLightTheme ? "#f7fafc" : "rgba(255, 255, 255, 0.03)",
    },
    summaryText: {
      color: isLightTheme ? "#5b6680" : "#cbd7ea",
      fontSize: "0.85rem",
    },
    summaryStrong: {
      color: isLightTheme ? "#172653" : "#ffffff",
    },
  }),
  [isLightTheme]
);

const getPopupSecondaryButtonStyle = (disabled) => ({
  border: isLightTheme ? "1px solid #bdcad8" : "1px solid rgba(99, 193, 199, 0.24)",
  color: isLightTheme ? "#223a7a" : "rgba(222, 232, 247, 0.95)",
  background: isLightTheme ? "#ffffff" : "rgba(255,255,255,0.06)",
  padding: "8px 12px",
  borderRadius: "10px",
  fontSize: "0.86rem",
  fontWeight: 700,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.65 : 1,
});

const getPopupPrimaryButtonStyle = (disabled) => ({
  border: isLightTheme ? "1px solid #223a7a" : "1px solid rgba(61, 182, 233, 0.8)",
  color: disabled
    ? isLightTheme
      ? "#8b95a8"
      : "rgba(130,144,166,0.85)"
    : isLightTheme
      ? "#ffffff"
      : "rgba(196,236,255,0.98)",
  background: disabled
    ? isLightTheme
      ? "#edf2f6"
      : "rgba(255,255,255,0.06)"
    : isLightTheme
      ? "#223a7a"
      : "rgba(61,182,233,0.16)",
  padding: "8px 12px",
  borderRadius: "10px",
  fontSize: "0.86rem",
  fontWeight: 800,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.65 : 1,
});

const getPopupStepChipStyle = (isActive, isDone) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "7px",
  padding: "6px 10px",
  borderRadius: "999px",
  border: isActive
    ? isLightTheme
      ? "1px solid #223a7a"
      : "1px solid rgba(61, 182, 233, 0.8)"
    : isLightTheme
      ? "1px solid #d7e2ec"
      : "1px solid rgba(255,255,255,0.16)",
  background: isActive
    ? isLightTheme
      ? "#223a7a"
      : "rgba(61, 182, 233, 0.16)"
    : isDone
      ? isLightTheme
        ? "#edf9f8"
        : "rgba(99, 193, 199, 0.1)"
      : isLightTheme
        ? "#f7fafc"
        : "rgba(255,255,255,0.04)",
  color: isActive
    ? isLightTheme
      ? "#ffffff"
      : "rgba(200, 236, 255, 0.98)"
    : isDone
      ? isLightTheme
        ? "#24666b"
        : "rgba(181, 222, 255, 0.95)"
      : isLightTheme
        ? "#5b6680"
        : "rgba(210, 222, 245, 0.72)",
  fontSize: "0.76rem",
  fontWeight: 800,
  letterSpacing: "0.25px",
  textTransform: "uppercase",
});

const visibleFilterOptions = React.useMemo(() => {
  if (!filterModel || filterModel.type !== "set") return [];
  const needle = filterOptionQuery.trim().toLowerCase();
  if (!needle) return filterModel.options;

  return filterModel.options.filter((o) => {
    const label = String(o.label || "").toLowerCase();
    const meta = String(o.meta || "").toLowerCase();
    return label.includes(needle) || meta.includes(needle);
  });
}, [filterModel, filterOptionQuery]);

const selectAllVisibleOptions = () => {
  if (!filterModel || filterModel.type !== "set") return;
  setColumnFilters((prev) => ({
    ...prev,
    [filterModel.key]: visibleFilterOptions.map((o) => o.value),
  }));
};

const filterPopoverPos = React.useMemo(() => {
  if (!activeFilter?.rect) return null;

  const rect = activeFilter.rect;
  const pad = 12;
  const viewportW = window.innerWidth || 1200;
  const viewportH = window.innerHeight || 800;
  const width = Math.min(380, viewportW - pad * 2);
  const estimatedHeight = 420;

  let left = rect.left;
  left = Math.max(pad, Math.min(left, viewportW - width - pad));

  const showBelow = rect.bottom + 10 + estimatedHeight <= viewportH - pad;
  let top = showBelow ? rect.bottom + 10 : rect.top - estimatedHeight - 10;
  top = Math.max(pad, Math.min(top, viewportH - estimatedHeight - pad));

  return { top, left, width };
}, [activeFilter?.rect]);

const filteredRateRows = React.useMemo(() => {
  const needle = debouncedQuickSearchQuery.trim().toLowerCase();
  const activeFiltersList = Object.entries(columnFilters).filter(([, value]) => Array.isArray(value) && value.length > 0);

  return rateRows.filter((row) => {
    if (needle && !row.searchIndex.includes(needle)) return false;

    for (const [key, selectedValues] of activeFiltersList) {
      const currentValue = row.filterValues[key];
      if (Array.isArray(currentValue)) {
        if (!currentValue.some((value) => selectedValues.includes(value))) return false;
      } else if (!selectedValues.includes(currentValue)) {
        return false;
      }
    }

    return true;
  });
}, [rateRows, debouncedQuickSearchQuery, columnFilters]);

useEffect(() => {
  if (viewMode !== "rates") closeFilterPopover();
}, [viewMode, closeFilterPopover]);

useEffect(() => {
  setFilterOptionQuery("");
}, [activeFilter?.key]);

useEffect(() => {
  if (!activeFilter?.key) return undefined;

  const onKeyDown = (e) => {
    if (e.key === "Escape") closeFilterPopover();
  };

  const onMouseDown = (e) => {
    if (filterPopoverRef.current?.contains(e.target)) return;
    if (e.target?.closest?.("[data-rm-filter-trigger]")) return;
    closeFilterPopover();
  };

  const onResize = () => closeFilterPopover();
  const onScroll = (e) => {
    if (filterPopoverRef.current?.contains(e.target)) return;
    closeFilterPopover();
  };

  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("mousedown", onMouseDown);
  window.addEventListener("resize", onResize);
  window.addEventListener("scroll", onScroll, true);

  return () => {
    window.removeEventListener("keydown", onKeyDown);
    window.removeEventListener("mousedown", onMouseDown);
    window.removeEventListener("resize", onResize);
    window.removeEventListener("scroll", onScroll, true);
  };
}, [activeFilter?.key, closeFilterPopover]);
const bulkBackgroundIsActive = ["queued", "running", "verifying"].includes(
  bulkBackgroundOperation?.phase
);
//Comenzamos desarrollo para poner una tabla que resuma todos los stop sell, para que se puedan encontrar fácilmente y borrar cuando sea necesario - 29.12.2025
  return (
    <div
      ref={pageViewportRef}
      className={`reservations-container rm-page rm-revenuePage${isLightTheme ? " rm-page--light" : ""}`}
    >
      {!demoModeActive && bulkBackgroundOperation && (
        <div
          className={`rm-bulkBackgroundStatus is-${bulkBackgroundOperation.phase}`}
          role={bulkBackgroundOperation.phase === "failed" ? "alert" : "status"}
          aria-live={bulkBackgroundOperation.phase === "failed" ? "assertive" : "polite"}
        >
          <span className="rm-bulkBackgroundDot" aria-hidden="true" />
          <div className="rm-bulkBackgroundCopy">
            <strong>{bulkBackgroundOperation.actionLabel}</strong>
            <span>{bulkBackgroundOperation.detail}</span>
            <div className="rm-bulkBackgroundTrack" aria-hidden="true">
              <span
                style={{
                  width: `${Math.max(
                    4,
                    Math.round(
                      (Number(bulkBackgroundOperation.completed || 0) /
                        Math.max(1, Number(bulkBackgroundOperation.total || 1))) *
                        100
                    )
                  )}%`,
                }}
              />
            </div>
          </div>
        </div>
      )}

      {viewMode === "rates" && (
        <div className="rm-rates-card">
          <div className="rm-rates-header">
            <div className="rm-rates-headingRow">
              <div>
                <div className="rm-rates-eyebrow">{rmCopy.stopSells.kicker}</div>
                <h3 className="rm-rates-title">{rmCopy.rates.title}</h3>
                <div className="rm-rates-subtitle">
                  {demoModeActive && demoRatesLoading ? demoCopy.loading
                    : demoModeActive && demoRatesError ? demoCopy.loadError
                    : rmCopy.rates.subtitleLoaded(ratePrices.length)}
                </div>
              </div>

              <div className="rm-rates-primaryAction">
                {(demoModeActive || permissions["revenue.create_room_rate"]) && (
                  <button
                    type="button"
                    className="rm-topBtn rm-topBtn--primary"
                    disabled={demoModeActive}
                    title={demoModeActive ? demoCopy.readOnly : undefined}
                    onClick={() => { if (!demoModeActive && !isDemoMode()) setShowNewRatePopup(true); }}
                  >
                    {rmCopy.rates.add}
                  </button>
                )}
              </div>
            </div>

            <div className="rm-rates-toolbar">
              <input
                className="rm-searchInput"
                value={quickSearchQuery}
                onChange={(e) => setQuickSearchQuery(e.target.value)}
                placeholder={rmCopy.rates.searchPlaceholder}
              />

              <div className="rm-topActions">
                <button
                  type="button"
                  className="rm-topBtn rm-topBtn--bulk"
                  onClick={handleOpenBulkApplyPopup}
                  disabled={demoModeActive || bulkBackgroundIsActive}
                  title={demoModeActive ? demoCopy.readOnly : undefined}
                >
                  {bulkBackgroundIsActive
                    ? rmCopy.bulk.backgroundAction
                    : rmCopy.rates.bulkApply}
                </button>

                <button
                  type="button"
                  className="rm-topBtn rm-topBtn--stopSell"
                  disabled={demoModeActive}
                  title={demoModeActive ? demoCopy.stopSellsUnavailable : undefined}
                  onClick={() => {
                    if (demoModeActive || isDemoMode()) return;
                    console.log("[REV-MGMT][ViewMode][Toggle]", { from: "rates", to: "stop_sells" });
                    setViewMode("stop_sells");
                  }}
                >
                  {rmCopy.rates.stopSellsOverview}
                </button>
              </div>
            </div>
          </div>

          <div
            ref={ratesTableWrapRef}
            className="rm-rates-table-wrap"
            style={{ height: `${ratesTableWrapHeight}px` }}
          >
            {demoModeActive && demoRatesLoading ? (
              <div role="status" style={{ padding: "20px" }}>{demoCopy.loading}</div>
            ) : demoModeActive && demoRatesError ? (
              <div role="alert" style={{ padding: "20px" }}>
                <p>{demoRatesError}</p>
                <button type="button" className="rm-topBtn" onClick={fetchRoomRates}>{demoCopy.retry}</button>
              </div>
            ) : (
            <table className="rm-rates-table">
              <colgroup>
                {permissions["revenue.view_price_detail"] && (
                  <col className="rm-col--roomRate" />
                )}
                <col className="rm-col--derived" />
                <col className="rm-col--client" />
                <col className="rm-col--category" />
                <col className="rm-col--status" />
                <col className="rm-col--adults" />
                <col className="rm-col--maxOccupancy" />
                <col className="rm-col--children" />
                <col className="rm-col--booking" />
                <col className="rm-col--refundable" />
                <col className="rm-col--penalty" />
                <col className="rm-col--money" />
                <col className="rm-col--money" />
                <col className="rm-col--stay" />
                <col className="rm-col--stay" />
                <col className="rm-col--date" />
                <col className="rm-col--date" />
              </colgroup>

              <thead>
                <tr>
                  {rateTableColumns.map((column) => {
                    const filterKey = column.key;
                    const isOpen = activeFilter?.key === filterKey;
                    const isFiltered = columnIsFiltered(filterKey);

                    return (
                    <th
                      key={column.key}
                      className={column.sticky}
                    >
                        <button
                          type="button"
                          data-rm-filter-trigger={filterKey}
                          className={`rm-thBtn ${isFiltered ? "rm-thBtn--filtered" : ""} ${isOpen ? "rm-thBtn--open" : ""}`}
                          onClick={(e) => toggleFilterPopover(filterKey, e.currentTarget)}
                          title={rmCopy.rates.filterBy(column.label)}
                        >
                          <span className="rm-thLabel">{column.label}</span>
                          <span className="rm-thSub">{columnSubtitle(filterKey)}</span>
                          <span className={`rm-thDot ${isFiltered ? "rm-thDot--on" : ""}`} aria-hidden="true" />
                        </button>
                    </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {filteredRateRows.map((row) => {
                  const { rate, roomCategoryText } = row;
                  let adjustmentText = "-";

                  if (rate.has_formula_link && rate.formula_inputs?.length) {
                    const abbrevMap = {
                      average: "AVG",
                      weighted: "WGT",
                      min: "MIN",
                      max: "MAX",
                      custom: "CST",
                    };
                    const shortType = abbrevMap[rate.formula_type] || "CST";
                    const baseCodes = rate.formula_inputs.map((f) => f.rate_code || "?").join(" + ");
                    adjustmentText = (
                      <div style={{ lineHeight: 1.1 }}>
                        <div className="rm-cell-main">{shortType}</div>
                        <div className="rm-cell-sub">{baseCodes}</div>
                      </div>
                    );
                  } else if (rate.adjustment_value != null && rate.adjustment_type) {
                    const numericAdjustment = Number.parseFloat(rate.adjustment_value);
                    if (Number.isFinite(numericAdjustment)) {
                      const arrow = numericAdjustment > 0 ? "UP" : numericAdjustment < 0 ? "DOWN" : "FLAT";
                      adjustmentText = `${arrow} ${formatRevenueAdjustmentValue(
                        numericAdjustment,
                        rate.adjustment_type,
                        resolveRevenueRateCurrency(rate, baseCurrency),
                        currencyCatalog
                      )}`;
                      const dependencyCodes = getTwoLevelDependencyCodes(rate);
                      if (dependencyCodes.length) adjustmentText += ` ${dependencyCodes.join(" -> ")}`;
                    }
                  }

                  return (
                    <tr key={rate.id}>
                      {permissions["revenue.view_price_detail"] && (
                        <td className="rm-sticky-1">
                          <button
                            className="rm-rate-open-btn"
                            type="button"
                            title={demoModeActive ? demoCopy.readOnly : undefined}
                            onClick={() => {
                              if (demoModeActive || isDemoMode()) {
                                navigate(`/edit-room-rate/${rate.id}?business_unit_id=${DEMO_BUSINESS_UNIT_ID}`);
                                return;
                              }
                              window.location.href = `/edit-room-rate/${rate.id}?business_unit_id=${businessUnitId}`;
                            }}
                          >
                            {rate?.name} ({rate?.rate_code})
                          </button>
                          <div className="rm-cell-sub">
                            {rate?.group_key ? `${rmCopy.values.group} ${rate.group_key}` : `${rmCopy.values.id} ${rate?.id}`}
                          </div>
                        </td>
                      )}

                      <td
                        className={permissions["revenue.view_price_detail"] ? "rm-sticky-2" : undefined}
                        style={{ whiteSpace: "normal", overflowWrap: "anywhere", wordBreak: "break-word" }}
                      >
                        {adjustmentText}
                      </td>

                      <td>{rate?.client?.name || rmCopy.values.noClient}</td>

                      <td title={roomCategoryText} style={{ whiteSpace: "normal", overflowWrap: "anywhere", wordBreak: "break-word" }}>
                        {roomCategoryText}
                      </td>

                      <td>
                        <span className={`rm-chip ${rate?.status === "active" ? "rm-chip--ok" : "rm-chip--off"}`}>
                          {formatRateStatus(rate?.status, rmCopy)}
                        </span>
                      </td>

                      <td><span className="rm-chip rm-chip--soft">{formatMatchValue(rate?.adults, rate?.adults_match_type, rmCopy.values.any, rmCopy)}</span></td>
                      <td><span className="rm-chip rm-chip--soft">{formatMatchValue(rate?.max_occupancy, rate?.max_occupancy_match_type, rmCopy.values.any, rmCopy)}</span></td>
                      <td><span className="rm-chip rm-chip--soft">{formatMatchValue(rate?.children, rate?.children_match_type, rmCopy.values.any, rmCopy)}</span></td>

                      <td>
                        <span className={`rm-chip ${rate?.show_in_booking_engine ? "rm-chip--ok" : "rm-chip--off"}`}>
                          {rate?.show_in_booking_engine ? rmCopy.values.yes : rmCopy.values.no}
                        </span>
                      </td>

                      <td>
                        <span className={`rm-chip ${rate?.is_refundable ? "rm-chip--ok" : "rm-chip--off"}`}>
                          {rate?.is_refundable ? rmCopy.values.yes : rmCopy.values.no}
                        </span>
                      </td>

                      <td>{rate?.penalty_type || rmCopy.values.none}</td>
                      <td>{formatRevenueCurrencyValue(rate?.min_price_amount, resolveRevenueRateCurrency(rate, baseCurrency), currencyCatalog)}</td>
                      <td>{formatRevenueCurrencyValue(rate?.max_price_amount, resolveRevenueRateCurrency(rate, baseCurrency), currencyCatalog)}</td>
                      <td>{rate?.default_min_stay ?? "-"}</td>
                      <td>{rate?.default_max_stay ?? "-"}</td>
                      <td className="rm-dateCell">{rate?.valid_from?.substring(0, 10) || "-"}</td>
                      <td className="rm-dateCell">{rate?.valid_to?.substring(0, 10) || "-"}</td>
                    </tr>
                  );
                })}
                {filteredRateRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={permissions["revenue.view_price_detail"] ? 17 : 16}
                      style={{ textAlign: "center", padding: "12px", color: isLightTheme ? "#72818b" : "rgba(220, 232, 250, 0.78)" }}
                    >
                      {rmCopy.rates.noMatch}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            )}
          </div>
        </div>
      )}

    {!demoModeActive && viewMode === "stop_sells" && (() => {
      const todayISO = (() => {
        const t = new Date();
        t.setHours(0, 0, 0, 0);
        return t.toISOString().slice(0, 10);
      })();

      const isoToDate = (iso) => {
        const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
        return new Date(y, (m || 1) - 1, d || 1);
      };

      const dateToISO = (dt) => {
        const y = dt.getFullYear();
        const m = String(dt.getMonth() + 1).padStart(2, "0");
        const d = String(dt.getDate()).padStart(2, "0");
        return `${y}-${m}-${d}`;
      };

      const nextDayISO = (iso) => {
        const dt = isoToDate(iso);
        dt.setDate(dt.getDate() + 1);
        return dateToISO(dt);
      };

      const normalizeISO = (raw) => {
        const iso = String(raw || "").slice(0, 10);
        return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : null;
      };

      const getLastUpdateISO = (ss) =>
        normalizeISO(ss?.updatedAt || ss?.updated_at || ss?.createdAt || ss?.created_at);

      const expandStopSellDates = (ss) => {
        if (Array.isArray(ss?.stay_dates) && ss.stay_dates.length > 0) {
          return ss.stay_dates
            .map((rawDate) => normalizeISO(rawDate))
            .filter(Boolean);
        }

        const stayDate = normalizeISO(ss?.stay_date);
        if (stayDate) return [stayDate];

        const startISO = normalizeISO(ss?.start_date);
        const endISO = normalizeISO(ss?.end_date || ss?.start_date);
        if (!startISO || !endISO || endISO < startISO) return [];

        const dates = [];
        const cursor = isoToDate(startISO);
        const end = isoToDate(endISO);
        while (cursor <= end) {
          dates.push(dateToISO(cursor));
          cursor.setDate(cursor.getDate() + 1);
          cursor.setHours(0, 0, 0, 0);
        }
        return dates;
      };

      const activeByDay = Object.values(
        (stopSellRows || []).reduce((acc, ss) => {
          const category =
            ss?.room_rate?.room_category?.name ||
            ss?.room_category?.name ||
            rmCopy.values.unknownCategory;

          const roomRateId = ss?.room_rate_id ?? ss?.room_rate?.id ?? null;
          const roomRateLabel = ss?.room_rate?.name
            ? `${ss.room_rate.name}${ss?.room_rate?.rate_code ? ` (${ss.room_rate.rate_code})` : ""}`
            : roomRateId != null
              ? `#${roomRateId}`
              : rmCopy.values.unknownRate;

          const rateKey = roomRateId != null ? `id:${roomRateId}` : `label:${roomRateLabel}`;
          const isAutomatic = ss?.is_automatic === true;
          const reason = String(ss?.reason || "").trim();
          const lastUpdateISO = getLastUpdateISO(ss);

          const stayDates = expandStopSellDates(ss).filter((stayDate) => stayDate >= todayISO);
          stayDates.forEach((stayDate) => {
            const key = `${category}__${rateKey}__${isAutomatic ? "1" : "0"}__${stayDate}`;
            if (!acc[key]) {
              acc[key] = {
                category,
                room_rate_id: roomRateId,
                room_rate_label: roomRateLabel,
                rate_key: rateKey,
                stay_date: stayDate,
                is_automatic: isAutomatic,
                last_update_iso: lastUpdateISO || null,
                _reasonSet: new Set(reason ? [reason] : []),
              };
            } else {
              if (reason) acc[key]._reasonSet.add(reason);
              if (lastUpdateISO && (!acc[key].last_update_iso || lastUpdateISO > acc[key].last_update_iso)) {
                acc[key].last_update_iso = lastUpdateISO;
              }
            }
          });

          return acc;
        }, {})
      ).map((dayRow) => ({
        ...dayRow,
        reasons: Array.from(dayRow._reasonSet || []),
      }));

      const groupedRanges = Object.values(
        activeByDay.reduce((acc, dayRow) => {
          const key = `${dayRow.category}__${dayRow.rate_key}__${dayRow.is_automatic ? "1" : "0"}`;
          if (!acc[key]) {
            acc[key] = {
              key,
              category: dayRow.category,
              room_rate_id: dayRow.room_rate_id,
              room_rate_label: dayRow.room_rate_label,
              rate_key: dayRow.rate_key,
              is_automatic: dayRow.is_automatic,
              days: [],
            };
          }
          acc[key].days.push(dayRow);
          return acc;
        }, {})
      )
        .sort((a, b) => {
          const byCategory = String(a.category || "").localeCompare(String(b.category || ""), sortLocale);
          if (byCategory !== 0) return byCategory;

          const byRate = String(a.room_rate_label || "").localeCompare(String(b.room_rate_label || ""), sortLocale);
          if (byRate !== 0) return byRate;

          return Number(a.is_automatic === true) - Number(b.is_automatic === true);
        })
        .flatMap((group) => {
          const sortedDays = [...group.days].sort((a, b) => String(a.stay_date).localeCompare(String(b.stay_date)));
          const ranges = [];

          for (const day of sortedDays) {
            const last = ranges[ranges.length - 1];
            if (last && nextDayISO(last.end_date) === day.stay_date) {
              last.end_date = day.stay_date;
              last.nights += 1;
              if (day.last_update_iso && (!last.last_update_iso || day.last_update_iso > last.last_update_iso)) {
                last.last_update_iso = day.last_update_iso;
              }
              (day.reasons || []).forEach((reasonValue) => {
                if (reasonValue) last._reasonSet.add(reasonValue);
              });
            } else {
              ranges.push({
                group_key: group.key,
                category: group.category,
                room_rate_id: group.room_rate_id,
                room_rate_label: group.room_rate_label,
                is_automatic: group.is_automatic,
                start_date: day.stay_date,
                end_date: day.stay_date,
                nights: 1,
                last_update_iso: day.last_update_iso || null,
                _reasonSet: new Set((day.reasons || []).filter(Boolean)),
              });
            }
          }

          return ranges.map((range, index) => {
            const reasonValues = Array.from(range._reasonSet || []);
            let reasonLabel = "-";
            if (reasonValues.length === 1) reasonLabel = reasonValues[0];
            if (reasonValues.length > 1) reasonLabel = rmCopy.values.multiple;

            return {
              ...range,
              reason_label: reasonLabel,
              row_key: `${range.group_key}__${range.start_date}__${range.end_date}__${index}`,
            };
          });
        });

      const categoriesAffected = new Set(groupedRanges.map((rangeRow) => rangeRow.category)).size;
      const ratesAffected = new Set(groupedRanges.map((rangeRow) => rangeRow.room_rate_id ?? rangeRow.room_rate_label)).size;
      const latestGlobalUpdate = groupedRanges.reduce((latest, rangeRow) => {
        if (!rangeRow.last_update_iso) return latest;
        if (!latest) return rangeRow.last_update_iso;
        return rangeRow.last_update_iso > latest ? rangeRow.last_update_iso : latest;
      }, null);

      return (
        <section className="rm-ss-stage">
          <header className="rm-ss-head">
            <div>
              <p className="rm-ss-kicker">{rmCopy.stopSells.kicker}</p>
              <h3 className="rm-ss-title">{rmCopy.stopSells.title}</h3>
              <p className="rm-ss-subtitle">
                {rmCopy.stopSells.subtitle}
              </p>
            </div>

            <div className="rm-ss-headActions">
              <button
                type="button"
                className="rm-ss-btn"
                onClick={() => {
                  console.log("[REV-MGMT][ViewMode][Toggle]", { from: "stop_sells", to: "rates" });
                  setViewMode("rates");
                }}
              >
                {rmCopy.stopSells.backToRates}
              </button>
            </div>
          </header>

          <div className="rm-ss-content">
            <div className="rm-ss-kpiGrid">
              <article className="rm-ss-kpiCard rm-ss-kpiCard--cyan">
                <div className="rm-ss-kpiLabel">{rmCopy.stopSells.kpis.activeNights.label}</div>
                <div className="rm-ss-kpiValue">{stopSellLoading ? "..." : activeByDay.length}</div>
                <div className="rm-ss-kpiFoot">{rmCopy.stopSells.kpis.activeNights.foot}</div>
              </article>

              <article className="rm-ss-kpiCard rm-ss-kpiCard--green">
                <div className="rm-ss-kpiLabel">{rmCopy.stopSells.kpis.activeRanges.label}</div>
                <div className="rm-ss-kpiValue">{stopSellLoading ? "..." : groupedRanges.length}</div>
                <div className="rm-ss-kpiFoot">{rmCopy.stopSells.kpis.activeRanges.foot}</div>
              </article>

              <article className="rm-ss-kpiCard rm-ss-kpiCard--blue">
                <div className="rm-ss-kpiLabel">{rmCopy.stopSells.kpis.categories.label}</div>
                <div className="rm-ss-kpiValue">{stopSellLoading ? "..." : categoriesAffected}</div>
                <div className="rm-ss-kpiFoot">{rmCopy.stopSells.kpis.categories.foot}</div>
              </article>

              <article className="rm-ss-kpiCard rm-ss-kpiCard--alert">
                <div className="rm-ss-kpiLabel">{rmCopy.stopSells.kpis.roomRates.label}</div>
                <div className="rm-ss-kpiValue">{stopSellLoading ? "..." : ratesAffected}</div>
                <div className="rm-ss-kpiFoot">{rmCopy.stopSells.kpis.roomRates.foot}</div>
              </article>

              <article className="rm-ss-kpiCard rm-ss-kpiCard--slate">
                <div className="rm-ss-kpiLabel">{rmCopy.stopSells.kpis.lastUpdate.label}</div>
                <div className="rm-ss-kpiValue">{stopSellLoading ? "..." : (latestGlobalUpdate || "-")}</div>
                <div className="rm-ss-kpiFoot">{rmCopy.stopSells.kpis.lastUpdate.foot}</div>
              </article>
            </div>

            <div className="rm-ss-tableWrap">
              {stopSellLoading ? (
                <div className="rm-ss-state">
                  <div>
                    <div className="rm-ss-stateTitle">{rmCopy.stopSells.loadingTitle}</div>
                    <div className="rm-ss-stateText">{rmCopy.stopSells.loadingText}</div>
                  </div>
                </div>
              ) : groupedRanges.length === 0 ? (
                <div className="rm-ss-state">
                  <div>
                    <div className="rm-ss-stateTitle">{rmCopy.stopSells.emptyTitle}</div>
                    <div className="rm-ss-stateText">{rmCopy.stopSells.emptyText}</div>
                  </div>
                </div>
              ) : (
                <table className="rm-ss-table">
                  <thead>
                    <tr>
                      <th>{rmCopy.stopSells.headers.roomCategory}</th>
                      <th>{rmCopy.stopSells.headers.roomRate}</th>
                      <th>{rmCopy.stopSells.headers.from}</th>
                      <th>{rmCopy.stopSells.headers.to}</th>
                      <th style={{ textAlign: "center" }}>{rmCopy.stopSells.headers.nights}</th>
                      <th>{rmCopy.stopSells.headers.automatic}</th>
                      <th>{rmCopy.stopSells.headers.reason}</th>
                      <th style={{ textAlign: "center" }}>{rmCopy.stopSells.headers.lastUpdate}</th>
                      <th style={{ textAlign: "center" }}>{rmCopy.stopSells.headers.action}</th>
                    </tr>
                  </thead>

                  <tbody>
                    {groupedRanges.map((rangeRow) => {
                      const openRateId = Number(rangeRow.room_rate_id);
                      const canOpenRate = Number.isFinite(openRateId);

                      return (
                        <tr key={rangeRow.row_key} className="rm-ss-row">
                          <td>{rangeRow.category}</td>
                          <td className="rm-ss-rateCell">
                            {rangeRow.room_rate_label}
                            <div className="rm-ss-muted">
                              {rangeRow.room_rate_id != null
                                ? `${rmCopy.values.id} ${rangeRow.room_rate_id}`
                                : rmCopy.values.rateWithoutId}
                            </div>
                          </td>
                          <td className="rm-ss-period">{rangeRow.start_date}</td>
                          <td className="rm-ss-period">{rangeRow.end_date}</td>
                          <td style={{ textAlign: "center" }}>{rangeRow.nights}</td>
                          <td>
                            <span className={`rm-ss-badge ${rangeRow.is_automatic ? "rm-ss-badge--auto" : "rm-ss-badge--manual"}`}>
                              {rangeRow.is_automatic ? rmCopy.values.yes : rmCopy.values.no}
                            </span>
                          </td>
                          <td>
                            <div className="rm-ss-reason" title={rangeRow.reason_label}>
                              {rangeRow.reason_label}
                            </div>
                          </td>
                          <td style={{ textAlign: "center" }}>{rangeRow.last_update_iso || "-"}</td>
                          <td style={{ textAlign: "center" }}>
                            <button
                              type="button"
                              className="rm-ss-openBtn"
                              disabled={!canOpenRate}
                              onClick={() => {
                                if (!canOpenRate) return;
                                console.log("[REV-MGMT][StopSells][OpenRate]", {
                                  room_rate_id: openRateId,
                                  business_unit_id: businessUnitId
                                });
                                window.location.href = `/edit-room-rate/${openRateId}?business_unit_id=${businessUnitId}`;
                              }}
                            >
                              {rmCopy.stopSells.openRate}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </section>
      );
    })()}



      {activeFilter?.key && filterModel && filterPopoverPos ? (
        <div
          ref={filterPopoverRef}
          className="rm-filterPopover"
          style={{ top: filterPopoverPos.top, left: filterPopoverPos.left, width: filterPopoverPos.width }}
        >
          <div className="rm-filterHeader">
            <div className="rm-filterTitle">{filterModel.title}</div>
            <div className="rm-filterHeaderActions">
              {columnIsFiltered(filterModel.key) ? (
                <button type="button" className="rm-filterSmallBtn" onClick={() => clearColumnFilter(filterModel.key)}>
                  {rmCopy.filters.clear}
                </button>
              ) : null}

              <button type="button" className="rm-filterSmallBtn" onClick={selectAllVisibleOptions}>
                {rmCopy.values.all}
              </button>

              <button type="button" className="rm-filterCloseBtn" onClick={closeFilterPopover}>
                x
              </button>
            </div>
          </div>

          <div className="rm-filterBody">
            <input
              className="rm-filterInput"
              value={filterOptionQuery}
              onChange={(e) => setFilterOptionQuery(e.target.value)}
              placeholder={rmCopy.filters.searchIn(filterModel.title)}
              autoFocus
            />

            <div className="rm-filterList">
              {visibleFilterOptions.length === 0 ? (
                <div className="rm-filterHint">{rmCopy.filters.noMatchingOptions}</div>
              ) : (
                visibleFilterOptions.map((opt) => {
                  const selected = Array.isArray(columnFilters[filterModel.key])
                    ? columnFilters[filterModel.key].includes(opt.value)
                    : false;

                  return (
                    <label key={String(opt.value)} className="rm-filterOption" title={opt.label}>
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleMultiSelectFilter(filterModel.key, opt.value)}
                      />
                      <span className="rm-filterOptionText">
                        <div className="rm-filterOptionLabel">{opt.label}</div>
                        {opt.meta ? <div className="rm-filterOptionMeta">{opt.meta}</div> : null}
                      </span>
                      <span className="rm-filterCountPill">{opt.count}</span>
                    </label>
                  );
                })
              )}
            </div>

            <div className="rm-filterHint">{rmCopy.filters.selectValuesHint}</div>
          </div>
        </div>
      ) : null}

{!demoModeActive && showNewRatePopup && (
  <div className="rm-modalOverlay rm-newRateOverlay" style={popupTheme.newRateOverlay}>
    <div className="rm-newRateModal" style={popupTheme.newRateModal}>
      <RoomRateForm
        businessUnitId={businessUnitId}
        onSuccess={(newRateId) => {
          setShowNewRatePopup(false);
          fetchRoomRates();
        }}

        onCancel={() => setShowNewRatePopup(false)}
      />
    </div>
  </div>
)}

{!demoModeActive && bulkFailureReport && (
  <div className="rm-modalOverlay rm-bulkFailureOverlay">
    <div
      className="rm-bulkFailureModal"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="rm-bulk-failure-title"
    >
      <div className="rm-bulkFailureHeader">
        <div>
          <span>{bulkFailureReport.actionLabel}</span>
          <h3 id="rm-bulk-failure-title">{rmCopy.bulk.backgroundFailureTitle}</h3>
          <p>
            {bulkFailureReport.startDate} → {bulkFailureReport.endDate}
          </p>
        </div>
      </div>

      <div className="rm-bulkFailureBody">
        <p>{rmCopy.bulk.backgroundFailureIntro}</p>
        <ul>
          {bulkFailureReport.failures.map((failure, failureIndex) => {
            const failedRate = (ratePrices || []).find(
              (rate) => Number(rate?.id) === Number(failure.room_rate_id)
            );
            const failedRateLabel =
              failure.room_rate_id == null
                ? rmCopy.values.unknownRate
                : failedRate
                  ? `${failedRate.name || rmCopy.values.unknownRate} (${failedRate.rate_code || `#${failure.room_rate_id}`})`
                  : `#${failure.room_rate_id}`;

            return (
              <li
                key={`bulk-failure-${bulkFailureReport.id}-${failure.room_rate_id ?? "unknown"}-${failureIndex}`}
              >
                <strong>{failedRateLabel}</strong>
                <span>{failure.error}</span>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="rm-bulkFailureFooter">
        <span>
          {rmCopy.bulk.backgroundFailed(
            bulkFailureReport.failures.length,
            bulkFailureReport.total
          )}
        </span>
        <button
          type="button"
          className="rm-modalBtn rm-modalBtn--primary"
          onClick={() => {
            setBulkFailureReport(null);
            setBulkBackgroundOperation((current) =>
              current?.id === bulkFailureReport.id ? null : current
            );
          }}
        >
          {rmCopy.bulk.backgroundDismiss}
        </button>
      </div>
    </div>
  </div>
)}

{/* 🆕 Bulk Apply Popup (wizard) */}
{!demoModeActive && showBulkApplyPopup && (
  <div
    className="rm-modalOverlay rm-bulkOverlay"
    style={popupTheme.overlay}
  >
    <div
      className="rm-bulkModal"
      style={popupTheme.modal}
    >
      <div className="rm-bulkHeader" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "12px" }}>
        <div>
          <div style={popupTheme.title}>
            {rmCopy.bulk.modalTitle}
          </div>
          <div style={popupTheme.subtitle}>
            {rmCopy.bulk.stepOf(bulkApplyStep, bulkStepTitles[bulkApplyStep])}
          </div>
        </div>

        <button
          type="button"
          className="rm-modalBtn rm-modalBtn--secondary"
          style={getPopupSecondaryButtonStyle(bulkApplySubmitting)}
          onClick={handleCloseBulkApplyPopup}
          disabled={bulkApplySubmitting}
        >
          {rmCopy.bulk.close}
        </button>
      </div>

      <div style={{ height: "10px" }} />

      <div className="rm-bulkSteps" style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "4px" }}>
        {[1, 2, 3, 4].map((step) => {
          const isActive = step === bulkApplyStep;
          const isDone = step < bulkApplyStep;
          return (
            <div
              key={`bulk-step-chip-${step}`}
              className={`rm-bulkStep${isActive ? " is-active" : ""}${isDone ? " is-done" : ""}`}
              style={getPopupStepChipStyle(isActive, isDone)}
            >
              <span>{step}</span>
              <span>{bulkStepTitles[step]}</span>
            </div>
          );
        })}
      </div>

      <div style={{ height: "8px" }} />

      {bulkApplyStep === 2 && (
      <>
      <div className="rm-bulkHelper" style={popupTheme.helperText}>
        {rmCopy.bulk.step2Title}
      </div>

      <div className="rm-bulkSearchRow" style={{ display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="text"
          value={bulkApplySearch}
          onChange={(e) => setBulkApplySearch(e.target.value)}
          placeholder={rmCopy.bulk.searchRatePlaceholder}
          style={popupTheme.searchRowInput}
        />

        <button
          type="button"
          className="rm-modalBtn rm-modalBtn--secondary"
          style={getPopupSecondaryButtonStyle(false)}
          onClick={() => {
            const qLocal = (bulkApplySearch || "").toLowerCase().trim();
            const allRows = (ratePrices || []).filter((r) => {
              if (!qLocal) return true;
              const label = `${r?.name || ""} (${r?.rate_code || ""})`.toLowerCase();
              return label.includes(qLocal);
            });
            const rows =
              bulkApplyAction === "upsert_rate_price"
                ? allRows.filter((r) => !isDerivedRateForPriceBulk(r))
                : allRows;
            const ids = rows.map((r) => r?.id).filter(Boolean);
            setBulkApplySelectedRateIds(ids);
            console.log("[REV-MGMT][BulkApply][SelectAll]", { count: ids.length, action: bulkApplyAction });
          }}
        >
          {rmCopy.bulk.selectAll}
        </button>

        <button
          type="button"
          className="rm-modalBtn rm-modalBtn--secondary"
          style={getPopupSecondaryButtonStyle(false)}
          onClick={() => {
            setBulkApplySelectedRateIds([]);
            console.log("[REV-MGMT][BulkApply][ClearSelection]");
          }}
        >
          {rmCopy.bulk.clearSelection}
        </button>
      </div>

      <div style={{ height: "12px" }} />

      <div className="rm-bulkList" style={popupTheme.listShell}>
        <div className="rm-bulkListHeader" style={popupTheme.listHeader}>
          <div />
          <div>{rmCopy.bulk.tableHeaders.roomRate}</div>
          <div>{rmCopy.bulk.tableHeaders.client}</div>
          <div>{rmCopy.bulk.tableHeaders.roomCategory}</div>
        </div>

        {(() => {
          const q = (bulkApplySearch || "").toLowerCase().trim();
          const allRows = (ratePrices || [])
            .filter(r => {
              if (!q) return true;
              const label = `${r?.name || ""} (${r?.rate_code || ""})`.toLowerCase();
              return label.includes(q);
            });
          const rows =
            bulkApplyAction === "upsert_rate_price"
              ? allRows.filter((r) => !isDerivedRateForPriceBulk(r))
              : allRows;
          const blockedCount = allRows.length - rows.length;

          if (rows.length === 0) {
            if (bulkApplyAction === "upsert_rate_price" && blockedCount > 0) {
              return (
                <div
                  className="rm-bulkWarning"
                  style={{ ...popupTheme.warning, borderBottom: "none" }}
                >
                  {rmCopy.bulk.derivedOnlyHidden}
                </div>
              );
            }
            return (
              <div className="rm-bulkEmpty" style={popupTheme.empty}>
                {rmCopy.bulk.noRatesMatch}
              </div>
            );
          }

          return (
            <>
              {bulkApplyAction === "upsert_rate_price" && blockedCount > 0 && (
                <div
                  className="rm-bulkWarning"
                  style={popupTheme.warning}
                >
                  {rmCopy.bulk.derivedHiddenCount(blockedCount)}
                </div>
              )}
              {rows.map((r) => {
                const id = r?.id;
                const checked = bulkApplySelectedRateIds.includes(id);
                return (
                  <div
                    key={`bulk-rate-${id}`}
                    className="rm-bulkListRow"
                    style={popupTheme.listRow}
                  >
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          const nextChecked = e.target.checked;
                          setBulkApplySelectedRateIds((prev) => {
                            if (nextChecked) return Array.from(new Set([...(prev || []), id]));
                            return (prev || []).filter(x => x !== id);
                          });
                        }}
                      />
                    </div>

                    <div style={{ fontWeight: 700 }}>
                      {r?.name} ({r?.rate_code})
                    </div>

                    <div style={popupTheme.listSubtleText}>
                      {r?.client?.name || rmCopy.values.notAvailable}
                    </div>

                    <div style={popupTheme.listSubtleText}>
                      {r?.room_category?.name || rmCopy.values.notAvailable}
                    </div>
                  </div>
                );
              })}
            </>
          );
        })()}
      </div>

      <div style={{ height: "14px" }} />
      </>
      )}

      {bulkApplyStep === 1 && (
        <div
          className="rm-bulkSection"
          style={popupTheme.section}
        >
          <div style={popupTheme.sectionTitle}>
            {rmCopy.bulk.step1Title}
          </div>
          <Select
            classNamePrefix="rm-select"
            value={bulkActionOptions.find((opt) => opt.value === bulkApplyAction) || null}
            onChange={(option) => setBulkApplyAction(option?.value || "")}
            options={bulkActionOptions}
            placeholder={rmCopy.bulk.selectActionPlaceholder}
            isClearable
            isSearchable={false}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              ...customSelectStyles,
              control: (provided, state) => ({
                ...provided,
                minHeight: "40px",
                background: isLightTheme ? "#ffffff" : "rgba(255, 255, 255, 0.05)",
                borderColor: isLightTheme
                  ? state.isFocused
                    ? "#3db6e9"
                    : "#bdcad8"
                  : state.isFocused
                    ? "rgba(61, 182, 233, 0.55)"
                    : "rgba(99, 193, 199, 0.22)",
                borderRadius: "10px",
                padding: "0 4px",
                boxShadow: state.isFocused
                  ? isLightTheme
                    ? "0 0 0 3px rgba(61, 182, 233, 0.2)"
                    : "0 0 0 3px rgba(61, 182, 233, 0.14)"
                  : "none",
              }),
              menu: (provided) => ({
                ...provided,
                backgroundColor: isLightTheme ? "#ffffff" : "#101a36",
                zIndex: 10050,
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: isLightTheme
                  ? state.isSelected
                    ? "#223a7a"
                    : state.isFocused
                      ? "#edf8fc"
                      : "#ffffff"
                  : state.isFocused
                    ? "#172653"
                    : "#101a36",
                color: isLightTheme
                  ? state.isSelected
                    ? "#ffffff"
                    : "#26354f"
                  : "#ffffff",
                fontSize: "0.9rem",
                cursor: "pointer",
              }),
              placeholder: (styles) => ({
                ...styles,
                color: isLightTheme ? "#7c879c" : "rgba(210, 222, 245, 0.72)",
                fontSize: "0.9rem",
              }),
              singleValue: (styles) => ({
                ...styles,
                color: isLightTheme ? "#26354f" : "rgba(239, 245, 255, 0.95)",
                fontSize: "0.9rem",
                opacity: 1,
              }),
              menuPortal: (base) => ({ ...base, zIndex: 10050 }),
            }}
          />
          <div className="rm-bulkHelper" style={popupTheme.helperText}>
            {rmCopy.bulk.chooseActionHelp}
          </div>
        </div>
      )}

      {bulkApplyStep === 3 && (
        <>
          <div style={{ height: "12px" }} />
          <div
            className="rm-bulkSection"
            style={popupTheme.section}
          >
            <div style={popupTheme.sectionTitle}>
              {rmCopy.bulk.step3Title}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px" }}>
              <div>
                <label style={popupTheme.label}>
                  {rmCopy.bulk.startDate}
                </label>
                <input
                  type="date"
                  value={bulkApplyStartDate}
                  onChange={(e) => setBulkApplyStartDate(e.target.value)}
                  style={popupTheme.input}
                />
              </div>
              <div>
                <label style={popupTheme.label}>
                  {rmCopy.bulk.endDate}
                </label>
                <input
                  type="date"
                  value={bulkApplyEndDate}
                  min={bulkApplyStartDate || undefined}
                  onChange={(e) => setBulkApplyEndDate(e.target.value)}
                  style={popupTheme.input}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {bulkApplyStep === 4 && (
        <>
          <div style={{ height: "12px" }} />
          <div
            className="rm-bulkSection"
            style={popupTheme.section}
          >
            <div style={popupTheme.sectionTitle}>
              {rmCopy.bulk.step4Title}
            </div>

            {!bulkApplyAction && (
              <div style={popupTheme.helperText}>
                {rmCopy.bulk.step1Hint}
              </div>
            )}

            {bulkApplyAction === "add_stop_sell" && (
              <div>
                <label style={popupTheme.label}>
                  {rmCopy.bulk.reasonOptional}
                </label>
                <input
                  type="text"
                  value={bulkStopSellReason}
                  onChange={(e) => setBulkStopSellReason(e.target.value)}
                  placeholder={rmCopy.bulk.reasonPlaceholder}
                  style={popupTheme.input}
                />
                <div style={popupTheme.helperText}>
                  {rmCopy.bulk.automaticFalseHint}
                </div>
              </div>
            )}

            {bulkApplyAction === "remove_stop_sell" && (
              <div style={popupTheme.helperText}>
                {rmCopy.bulk.noExtraInputs}
              </div>
            )}

            {bulkApplyAction === "upsert_rate_price" && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px" }}>
                <div>
                  <label style={popupTheme.label}>
                    {rmCopy.bulk.price} ({bulkRateCurrency})
                  </label>
                  <input
                    type="number"
                    inputMode={bulkRateMinorUnits === 0 ? "numeric" : "decimal"}
                    min="0"
                    step={currencyAmountStep(bulkRateCurrency, currencyCatalog)}
                    value={bulkRatePriceValue}
                    onChange={(e) => setBulkRatePriceValue(e.target.value)}
                    style={popupTheme.input}
                  />
                </div>
                <div>
                  <label style={popupTheme.label}>
                    {rmCopy.bulk.minStayOptional}
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={bulkRatePriceMinStay}
                    onChange={(e) => setBulkRatePriceMinStay(e.target.value)}
                    style={popupTheme.input}
                  />
                </div>
                <div>
                  <label style={popupTheme.label}>
                    {rmCopy.bulk.maxStayOptional}
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={bulkRatePriceMaxStay}
                    onChange={(e) => setBulkRatePriceMaxStay(e.target.value)}
                    style={popupTheme.input}
                  />
                </div>
              </div>
            )}

            {bulkApplyAction === "set_min_stay" && (
              <div>
                <label style={popupTheme.label}>
                  {rmCopy.bulk.minStay}
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={bulkMinStayValue}
                  onChange={(e) => setBulkMinStayValue(e.target.value)}
                  style={popupTheme.input}
                />
              </div>
            )}

            {bulkApplyAction === "set_max_stay" && (
              <div>
                <label style={popupTheme.label}>
                  {rmCopy.bulk.maxStay}
                </label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={bulkMaxStayValue}
                  onChange={(e) => setBulkMaxStayValue(e.target.value)}
                  style={popupTheme.input}
                />
              </div>
            )}
          </div>
        </>
      )}

      <div style={{ height: "14px" }} />

      <div className="rm-bulkFooter" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "12px" }}>
        <div className="rm-bulkSummary" style={popupTheme.summaryText}>
          {rmCopy.bulk.selectedLabel}: <strong style={popupTheme.summaryStrong}>{bulkApplySelectedRateIds.length}</strong>
          {" | "}
          {rmCopy.bulk.actionLabel}: <strong style={popupTheme.summaryStrong}>{bulkActionLabels[bulkApplyAction] || rmCopy.values.notAvailable}</strong>
        </div>

        <div className="rm-bulkFooterActions" style={{ display: "flex", gap: "8px" }}>
          <button
            type="button"
            className="rm-modalBtn rm-modalBtn--secondary"
            onClick={handleCloseBulkApplyPopup}
            style={getPopupSecondaryButtonStyle(bulkApplySubmitting)}
            disabled={bulkApplySubmitting}
          >
            {rmCopy.bulk.cancel}
          </button>

          {bulkApplyStep > 1 && (
            <button
              type="button"
              className="rm-modalBtn rm-modalBtn--secondary"
              onClick={() => setBulkApplyStep((s) => Math.max(1, s - 1))}
              style={getPopupSecondaryButtonStyle(bulkApplySubmitting)}
              disabled={bulkApplySubmitting}
            >
              {rmCopy.bulk.back}
            </button>
          )}

          {bulkApplyStep < 4 && (
            <button
              type="button"
              className="rm-modalBtn rm-modalBtn--primary"
              disabled={
                bulkApplySubmitting ||
                (bulkApplyStep === 1 && !bulkApplyAction) ||
                (bulkApplyStep === 2 && bulkApplySelectedRateIds.length === 0) ||
                (bulkApplyStep === 3 && !isBulkApplyDateRangeValid())
              }
              style={getPopupPrimaryButtonStyle(
                bulkApplySubmitting ||
                  (bulkApplyStep === 1 && !bulkApplyAction) ||
                  (bulkApplyStep === 2 && bulkApplySelectedRateIds.length === 0) ||
                  (bulkApplyStep === 3 && !isBulkApplyDateRangeValid())
              )}
              onClick={handleBulkApplyNextStep}
            >
              {rmCopy.bulk.next}
            </button>
          )}

          {bulkApplyStep === 4 && (
            <button
              type="button"
              className="rm-modalBtn rm-modalBtn--primary"
              disabled={
                bulkApplySubmitting ||
                bulkApplySelectedRateIds.length === 0 ||
                !bulkApplyAction ||
                !isBulkApplyDateRangeValid() ||
                !isBulkApplyStep4Ready()
              }
              style={getPopupPrimaryButtonStyle(
                bulkApplySubmitting ||
                  bulkApplySelectedRateIds.length === 0 ||
                  !bulkApplyAction ||
                  !isBulkApplyDateRangeValid() ||
                  !isBulkApplyStep4Ready()
              )}
              onClick={handleBulkApplySubmit}
            >
              {bulkApplySubmitting ? rmCopy.bulk.applying : rmCopy.bulk.apply}
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
)}



    </div>
  );
    
  
};

export default RevenueManagement;
